<template>
  <div class="setup-page app-page">
    <header class="app-page-header setup-header">
      <div class="app-page-header-tabs">
        <button
          v-for="step in steps"
          :key="step.id"
          class="step"
          :class="{ active: activeStep === step.id, done: stepStatus(step.id) === 'done' }"
          :disabled="step.id > highestReachableStep"
          @click="goToStep(step.id)"
        >
          <span class="step-check" :class="stepStatus(step.id)"></span>
          {{ step.label }}
          <span v-if="stepStatus(step.id) === 'done'" class="step-done-mark">✓</span>
        </button>
      </div>
      <div class="setup-header-progress" v-if="step1Generating">
        <div class="setup-header-progress-bar"><span :style="{ width: liveStepProgress + '%' }"></span></div>
        <span class="setup-header-progress-text">{{ liveStepProgress }}% · {{ step1ProgressMessage || '生成中…' }}</span>
      </div>
      <!-- 离线写入队列徽章：网络异常累积的草稿写请求数。点开后用户可手动重试 / 清空 -->
      <div v-if="offlineQueuePending > 0" class="setup-offline-badge">
        <span class="offline-badge-text">离线兜底：{{ offlineQueuePending }} 条待重发</span>
        <button type="button" class="offline-badge-btn" @click="retryOfflineQueueNow">重试</button>
        <button type="button" class="offline-badge-btn ghost" @click="clearOfflineQueue">丢弃</button>
      </div>
    </header>

    <div class="setup-fixed-actions">
      <button
        v-if="step1Generated"
        type="button"
        class="secondary-btn slim-btn"
        @click="openScriptPreviewModal"
      >
        预览剧本
      </button>
      <button
        v-if="step1Generated && activeStep === 1"
        type="button"
        class="secondary-btn slim-btn"
        :disabled="step1RegenerateDisabled"
        @click="handleStep1RegenerateReset"
      >
        {{
          step1Regenerating || step1Generating
            ? "生成中…"
            : `重新生成剧本（${step1PointsLabel}）`
        }}
      </button>
      <template v-if="activeStep === 1">
        <button
          v-if="!step1Locked && !step1Generated"
          type="button"
          class="main-btn slim-btn"
          :disabled="step1GenerateDisabled"
          @click="handleStep1Generate"
        >
          {{
            step1Generating
              ? "生成中…"
              : `开始生成（${step1PointsLabel}）`
          }}
        </button>
        <span v-if="!step1Locked && !step1Generated && step1Generating" class="step1-processing-tip">
          {{ step1ProgressMessage || "处理中（可离开页面，刷新后会自动继续监听）" }}
        </span>
        <button
          v-if="step1Generated"
          type="button"
          class="main-btn slim-btn"
          @click="goToStep(2)"
        >
          下一步
        </button>
      </template>
      <template v-else>
        <button v-if="activeStep > 1" type="button" class="secondary-btn slim-btn" @click="goToStep(activeStep - 1)">
          上一步
        </button>
        <button
          v-if="activeStep < 3"
          type="button"
          class="main-btn slim-btn"
          :disabled="activeStep === 2 && !step2Ready"
          @click="goToStep(activeStep + 1)"
        >
          下一步
        </button>
        <!-- “进入分镜编辑”按钮移除：每集卡片的“编辑”会进入对应编辑页 -->
      </template>
    </div>

    <div v-if="activeStep === 1" class="setup-body step-fill">
      <aside class="setup-sidebar">
        <div class="project-info-card">
          <div class="setup-label">项目名称</div>
          <input
            v-model.trim="project.title"
            class="setup-title-input"
            maxlength="50"
            placeholder="请输入项目名称"
            @blur="persistProjectTitle"
          />
          <div class="project-info-meta">
            <span class="project-info-chip">画幅：{{ project.ratio || '—' }}</span>
            <span class="project-info-chip">类型：{{ project.type || '—' }}</span>
            <span class="project-info-chip">语言：{{ projectVideoLang === 'en' ? '英文' : '中文' }}</span>
            <span class="project-info-chip project-info-chip--style">画风：{{ activeStyleLabel || '—' }}</span>
          </div>
          <p class="setup-hint">画幅、类型与画面风格会影响步骤一/四的提示词生成，可在下方修改。</p>
        </div>
        <div class="setup-block">
          <div class="setup-label">画幅</div>
          <div class="setup-select-grid setup-ratio-grid">
            <button
              v-for="item in ratioOptions"
              :key="item.value"
              type="button"
              class="setup-select-card"
              :class="{ active: project.ratio === item.value }"
              @click="setProjectRatio(item.value)"
            >
              {{ item.label }}
            </button>
          </div>
        </div>
        <div class="setup-block">
          <div class="setup-label">类型</div>
          <div class="setup-select-grid">
            <button
              v-for="item in typeOptions"
              :key="item.value"
              type="button"
              class="setup-select-card setup-type-card"
              :class="{ active: project.type === item.value }"
              @click="setProjectType(item.value)"
            >
              <strong>{{ item.label }}</strong>
              <span>{{ item.desc }}</span>
            </button>
          </div>
        </div>
        <div class="setup-block">
          <div class="setup-label">画面风格 + 参考对象</div>
          <div class="setup-hint">不同「类型」下会优先展示更贴合该片种的画风，您可直接点选卡片切换。</div>
          <div class="style-grid">
            <div
              v-for="item in step1StyleOptions"
              :key="item.id"
              class="style-card"
              :class="{ active: item.id === activeStyle }"
              @click="setActiveStyle(item.id)"
            >
              <img
                v-if="String(item.cover || '').trim()"
                class="style-card-cover"
                :src="item.cover"
                :alt="item.name"
                @error="item.cover = ''"
              />
              <div v-else class="style-card-cover placeholder-block"></div>
              <span>{{ item.name }}</span>
            </div>
          </div>
          <div v-if="step1StyleReferences && step1StyleReferences.length" class="style-reference-block">
            <div class="setup-label style-reference-label">画风参考对象</div>
            <div class="style-reference-grid">
              <div
                v-for="(ref, ri) in step1StyleReferences"
                :key="ri"
                class="style-reference-item"
                :title="ref.name"
              >
                <img v-if="ref.cover" :src="ref.cover" :alt="ref.name" />
                <div v-else class="placeholder-block style-reference-cover"></div>
                <span>{{ ref.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <section class="setup-main">
        <h2>剧本</h2>
        <p class="hint">上传您的剧本或小说，开始创作</p>
        <div class="script-type-row">
          <button
            v-for="type in scriptTypes"
            :key="type.value"
            type="button"
            class="script-type"
            :class="{ active: activeScriptType === type.value, disabled: scriptTypeLocked }"
            @click="onScriptTypeClick(type.value)">
            {{ type.label }}
          </button>
        </div>
        <p
          v-if="!step1Locked && !step1Generated && activeScriptType === 'ai_generate_script_agent'"
          class="hint"
        >
          AI生成通常需 1～3 分钟，请耐心等待。
        </p>
        <textarea
          v-model.trim="scriptContent"
          class="script-editor"
          :class="{ disabled: step1Locked }"
          :placeholder="scriptPlaceholder"
          :readonly="step1Locked"
        />
        <div
          v-if="!step1Locked && !step1Generated && activeScriptType === 'upload_script'"
          class="script-char-count"
          :class="{ warn: scriptContentNearLimit, over: scriptContentOverLimit }"
        >
          {{ scriptContentCharCount }} / {{ step1ScriptMaxChars }} 字
          <span v-if="scriptContentOverLimit" class="script-char-hint">超出上限，请删减或分段创作后再提交</span>
          <span v-else-if="scriptContentNearLimit" class="script-char-hint">接近上限，建议精简后提交以获得更稳定的分镜</span>
        </div>
        <div v-if="!step1Locked" class="script-episode-block">
          <div class="setup-label">预设集数</div>
          <p class="hint episode-count-hint">
            <!--与后台步骤一一致：-->
            智能集数约 {{ step1EpisodeSmartDefault }} 集（每次生成在 {{ step1EpisodeSmartMin }}～{{ step1EpisodeSmartMax }} 集间随机）；自定义上限 {{ step1EpisodeCustomMax }} 集（超出会自动收敛）。
          </p>
          <div class="episode-count-row">
            <div class="episode-radios">
              <label class="episode-radio-label">
                <input v-model="episodeCountMode" type="radio" value="smart" name="episodeCountMode" />
                <span>智能集数</span>
              </label>
              <div class="episode-custom-inline">
                <label class="episode-radio-label">
                  <input v-model="episodeCountMode" type="radio" value="custom" name="episodeCountMode" />
                  <span>自定义集数</span>
                </label>
                <div
                  v-show="episodeCountMode === 'custom'"
                  class="episode-count-input-wrap"
                >
                  <input
                    v-model.number="customEpisodeCount"
                    type="number"
                    min="1"
                    :max="step1EpisodeCustomMax"
                    class="episode-count-input"
                    @change="clampCustomEpisodeCount"
                    @blur="clampCustomEpisodeCount"
                  />
                  <span class="episode-count-suffix">集</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div v-else-if="activeStep === 2" class="role-layout step-fill">
      <aside class="role-list-panel">
        <div class="role-panel-header">
          <h3>{{ resourcePanelTitle }}</h3>
          <div class="role-header-actions">
            <label class="role-filter-toggle" :class="{ on: resourceFilterUngenerated }" v-if="false">
              <input
                type="checkbox"
                v-model="resourceFilterUngenerated"
              />
              <span>仅看未生成</span>
              <em v-if="resourceFilterUngenerated">({{ currentResourceUngeneratedCount }})</em>
            </label>
            <button class="secondary-btn slim-btn" @click="openRoleModal()" >+ 添加资源</button>
            <button
              class="secondary-btn slim-btn"
              :disabled="isCurrentTabBatchBusy || (resourceFilterUngenerated && !currentResourceUngeneratedCount)"
              @click="handleBatchGenerate"
            >
              {{
                isCurrentTabBatchBusy
                  ? "批量生成中…"
                  : `批量生成（${batchImagePointsSummary}）`
              }}
            </button>
          </div>
        </div>
        <div v-if="currentTabStep2WatchCount > 0" class="step2-task-queue-hint">
          {{ step2QueueHintText }}
        </div>
        <div class="resource-subnav" role="tablist">
          <button
            type="button"
            class="resource-subnav-item"
            :class="{ active: resourceSubTab === 'role' }"
            role="tab"
            :aria-selected="resourceSubTab === 'role'"
            @click="setResourceSubTab('role')"
          >
            角色
          </button>
          <button
            type="button"
            class="resource-subnav-item"
            :class="{ active: resourceSubTab === 'scene' }"
            role="tab"
            :aria-selected="resourceSubTab === 'scene'"
            @click="setResourceSubTab('scene')"
          >
            场景
          </button>
          <button
            type="button"
            class="resource-subnav-item"
            :class="{ active: resourceSubTab === 'material' }"
            role="tab"
            :aria-selected="resourceSubTab === 'material'"
            @click="setResourceSubTab('material')"
          >
            素材
          </button>
        </div>
        <div v-if="!currentResourceList.length" class="empty-panel">暂无数据</div>
        <div v-else-if="resourceFilterUngenerated && !currentResourceUngeneratedCount" class="empty-panel">
          🎉 当前分类下无未生成项
        </div>
        <div v-else class="role-list">
          <div
            v-for="item in displayedResourceList"
            :key="`${resourceSubTab}-${item.id}`"
            class="role-list-item"
            :class="{ active: isResourceItemActive(item) }"
            @click="selectResourceItem(item)"
          >
            <div v-if="isPlaceholderAsset(getResourceThumb(item))" class="role-list-thumb placeholder-block"></div>
            <img v-else class="role-list-thumb" :src="getResourceThumb(item)" :alt="item.name" @error="onImgError(item, 'avatar')" />
            <div class="role-list-content">
              <div class="role-list-top">
                <strong>{{ item.name }}</strong>
                <span class="role-status">{{ itemPreviewLabel(item) }}</span>
              </div>
              <p>{{ item.summary }}</p>
            </div>
            <button
              type="button"
              class="role-delete-icon"
              title="删除角色"
              @click.stop="confirmDeleteRole(item)"  v-if="false"
            >
              🗑
            </button>
            <button
              type="button"
              class="role-asset-icon"
              title="添加到资产库"
              @click.stop="addResourceToAssetLibrary(item)"
            >
              📦
            </button>
          </div>
        </div>
      </aside>

      <section class="role-preview-panel">
        <div v-if="currentResourceEntity" class="role-preview-wrap">
          <img
            v-if="currentResourceEntity.preview && !isPlaceholderAsset(currentResourceEntity.preview)"
            class="role-preview-image"
            :src="currentResourceEntity.preview"
            :alt="currentResourceEntity.name"
            @click="openEntityPreview(currentResourceEntity)"
            @error="onImgError(currentResourceEntity, 'preview')"
          />
          <div v-else-if="currentResourceEntity.preview" class="role-preview-image placeholder-block"></div>
          <div v-else class="role-preview-placeholder">{{ previewPlaceholderText }}</div>
          <div class="role-preview-actions">
            <input
              ref="entityUploadInput"
              type="file"
              accept="image/*"
              style="display: none"
              @change="onEntityImageSelected"
            />
            <button class="secondary-btn slim-btn" @click="triggerEntityImageUpload">上传图片</button>
            <button class="secondary-btn slim-btn" v-if="false">对话改图</button>
            <button class="secondary-btn slim-btn" :disabled="!entityHasPreviewImage" @click="downloadCurrentEntityImage">
              下载图片
            </button>
            <button class="secondary-btn slim-btn" v-if="false">生成记录</button>
          </div>
        </div>
        <div v-else class="empty-panel role-empty">暂无数据</div>
      </section>

      <aside class="role-detail-panel">
        <h3>{{ resourceDetailTitle }}</h3>
        <div v-if="currentResourceEntity && resourceSubTab === 'role'" class="role-detail-form">
          <div class="detail-field">
            <label>角色名</label>
            <input v-model="currentResourceEntity.name" maxlength="20" />
          </div>
          <div class="detail-field">
            <label>年龄</label>
            <input v-model="currentResourceEntity.age" maxlength="20" />
          </div>
          <div class="detail-field">
            <label>性别</label>
            <input v-model="currentResourceEntity.gender" maxlength="20" />
          </div>
          <div class="detail-field">
            <label>描述</label>
            <textarea v-model="currentResourceEntity.description" rows="14" :placeholder="entityDescriptionPlaceholder" />
          </div>
          <div class="detail-actions">
            <button class="secondary-btn" @click="saveCurrentResource">保存角色信息</button>
            <button class="main-btn" :disabled="step2ImageWorkLocked" @click="regenerateCurrentResource">
              {{ step2CurrentEntityBusyLabel }}<span class="role-regenerate-points">（{{ step2SinglePointsLabel }}）</span>
            </button>
          </div>
        </div>
        <div v-else-if="currentResourceEntity && resourceSubTab === 'scene'" class="role-detail-form">
          <div class="detail-field">
            <label>场景名</label>
            <input v-model="currentResourceEntity.name" maxlength="20" />
          </div>
          <div class="detail-field">
            <label>摘要</label>
            <input v-model="currentResourceEntity.summary" maxlength="100" />
          </div>
          <div class="detail-field">
            <label>描述</label>
            <textarea v-model="currentResourceEntity.description" rows="14" :placeholder="entityDescriptionPlaceholder" />
          </div>
          <div class="detail-actions">
            <button class="secondary-btn" @click="saveCurrentResource">保存场景信息</button>
            <button class="main-btn" :disabled="step2ImageWorkLocked" @click="regenerateCurrentResource">
              {{ step2CurrentEntityBusyLabel }}<span class="role-regenerate-points">（{{ step2SinglePointsLabel }}）</span>
            </button>
          </div>
        </div>
        <div v-else-if="currentResourceEntity && resourceSubTab === 'material'" class="role-detail-form">
          <div class="detail-field">
            <label>素材名</label>
            <input v-model="currentResourceEntity.name" maxlength="20" />
          </div>
          <div class="detail-field">
            <label>摘要</label>
            <input v-model="currentResourceEntity.summary" maxlength="100" />
          </div>
          <div class="detail-field">
            <label>描述</label>
            <textarea v-model="currentResourceEntity.description" rows="14" :placeholder="entityDescriptionPlaceholder" />
          </div>
          <div class="detail-actions">
            <button class="secondary-btn" @click="saveCurrentResource">
              保存素材信息
            </button>
            <button class="main-btn" :disabled="step2ImageWorkLocked" @click="regenerateCurrentResource">
              {{ step2CurrentEntityBusyLabel }}<span class="role-regenerate-points">({{ step2SinglePointsLabel }})</span>
            </button>
          </div>
        </div>
        <div v-else class="empty-panel">暂无数据</div>
      </aside>
    </div>

    <div v-else class="storyboard-layout step-fill">
      <div class="storyboard-header">
        <div class="storyboard-header-left">
          <h2>共 {{ storyboards.length }} 集</h2>
          <p class="hint">点击「进入分镜」打开对应集数页面；可一次性批量提交所有未生成分镜视频。</p>
        </div>
        <div class="storyboard-header-actions">
          <div class="storyboard-view-toggle" role="tablist">
            <button
              type="button"
              class="storyboard-view-toggle-item"
              :class="{ active: storyboardViewMode === 'card' }"
              @click="storyboardViewMode = 'card'"
              title="卡片视图"
            >▦ 卡片</button>
            <button
              type="button"
              class="storyboard-view-toggle-item"
              :class="{ active: storyboardViewMode === 'table' }"
              @click="storyboardViewMode = 'table'"
              title="表格视图"
            >▤ 表格</button>
          </div>
          <button type="button" class="secondary-btn slim-btn" @click="handleBatchGenerateStoryboardVideos" :disabled="batchSending || !storyboards.length">
            {{ batchSending ? '排队中…' : '批量提交全部分镜视频' }}
          </button>
          <button type="button" class="secondary-btn slim-btn" @click="openEpisodeModal" v-if="false">新增分集</button>
        </div>
      </div>

      <div class="storyboard-content-scroll">
        <div v-if="!storyboards.length" class="empty-panel storyboard-empty">暂无数据</div>
        <div v-else-if="storyboardViewMode === 'table'" class="storyboard-table">
          <div class="storyboard-table-head">
            <div class="storyboard-table-cell storyboard-table-cell--no">#</div>
            <div class="storyboard-table-cell storyboard-table-cell--title">分集标题</div>
            <div class="storyboard-table-cell storyboard-table-cell--summary">简介</div>
            <div class="storyboard-table-cell storyboard-table-cell--count">镜头数</div>
            <div class="storyboard-table-cell storyboard-table-cell--ops">操作</div>
          </div>
          <div
            v-for="(item, index) in storyboards"
            :key="'row-' + item.id"
            class="storyboard-table-row"
          >
            <div class="storyboard-table-cell storyboard-table-cell--no">{{ index + 1 }}</div>
            <div class="storyboard-table-cell storyboard-table-cell--title"><strong>{{ item.title }}</strong></div>
            <div class="storyboard-table-cell storyboard-table-cell--summary"><span v-if="item.summary">{{ item.summary }}</span><span v-else class="hint">—</span></div>
            <div class="storyboard-table-cell storyboard-table-cell--count">{{ Array.isArray(item.shots) ? item.shots.length : 0 }}</div>
            <div class="storyboard-table-cell storyboard-table-cell--ops">
              <button class="story-btn" @click="openStoryboardEdit(item)">进入分镜</button>
              <button
                class="story-btn"
                :disabled="isEpisodeRegenerating(item)"
                @click="regenerateEpisode(item)"
                v-if="hasUserToken"
              >{{ isEpisodeRegenerating(item) ? '重生中…' : '重生' }}</button>
              <div v-if="episodeRegenProgressFor(item)" class="story-btn-progress">
                <div class="story-btn-progress-bar">
                  <div class="story-btn-progress-fill" :style="{ width: (episodeRegenProgressFor(item).progress || 0) + '%' }"></div>
                </div>
                <span class="story-btn-progress-text">{{ episodeRegenProgressFor(item).message || '处理中…' }} {{ episodeRegenProgressFor(item).progress || 0 }}%</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="storyboard-grid">
          <div v-for="(item, index) in storyboards" :key="item.id" class="storyboard-card">
            <div class="storyboard-index">{{ index + 1 }}</div>
            <div
              v-if="isPlaceholderAsset(item.cover)"
              class="storyboard-cover placeholder-block"
              @click="openStoryboardPreview(item)"
            ></div>
            <img
              v-else
              class="storyboard-cover"
              :src="item.cover"
              :alt="item.title"
              @click="openStoryboardPreview(item)"
              @error="onImgError(item, 'cover')"
            />
            <div class="storyboard-content">
              <h3 @click.stop="openStoryboardEdit(item)">{{ item.title }}</h3>
              <!-- 移除 roles/scenes/duration 展示 -->
              <p class="storyboard-summary" v-if="item.summary">{{ item.summary }}</p>
              <div class="storyboard-actions">
                <button class="story-btn" @click.stop="openStoryboardEdit(item)">进入分镜</button>
                <button class="story-btn primary" @click.stop="openStoryboardExport(item)">导出</button>
                <button
                  class="story-btn"
                  :disabled="isEpisodeRegenerating(item)"
                  @click.stop="regenerateEpisode(item)"
                  v-if="hasUserToken"
                >{{ isEpisodeRegenerating(item) ? '重生中…' : '重生本集' }}</button>
                <button class="story-btn story-btn-danger" @click.stop="confirmDeleteStoryboard(item)">删除</button>
                <div v-if="episodeRegenProgressFor(item)" class="story-btn-progress story-btn-progress--block">
                  <div class="story-btn-progress-bar">
                    <div class="story-btn-progress-fill" :style="{ width: (episodeRegenProgressFor(item).progress || 0) + '%' }"></div>
                  </div>
                  <span class="story-btn-progress-text">{{ episodeRegenProgressFor(item).message || '处理中…' }} {{ episodeRegenProgressFor(item).progress || 0 }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="regenQueue && regenQueue.length" class="regen-toast" role="status" aria-live="polite">
      <div class="regen-toast-header">
        <strong>分集重生进度</strong>
        <span class="regen-toast-count">{{ activeRegenCount }} / {{ regenQueue.length }} 进行中</span>
      </div>
      <div
        v-for="(job, idx) in regenQueue"
        :key="'regen-' + (job.taskId || (job.episodeId || idx))"
        class="regen-toast-row"
        :class="regenStatusClass(job.status)"
      >
        <span class="regen-toast-title">第 {{ job.episodeNo || '?' }} 集「{{ job.episodeTitle || '' }}」</span>
        <span class="regen-toast-status">{{ regenStatusLabel(job.status) }}</span>
        <div class="regen-toast-bar">
          <div class="regen-toast-fill" :style="{ width: (job.progress || 0) + '%' }"></div>
        </div>
        <span class="regen-toast-message">{{ job.message || '处理中…' }}</span>
      </div>
    </div>

    <div v-if="confirmModal.visible" class="mask" @click.self="closeConfirmModal">
      <div class="modal confirm-modal">
        <h3>{{ confirmModal.title }}</h3>
        <p class="confirm-message">{{ confirmModal.message }}</p>
        <div class="modal-actions">
          <button type="button" class="secondary-btn" @click="closeConfirmModal">{{ confirmModal.cancelText || "取消" }}</button>
          <button type="button" class="primary-btn" @click="confirmModalOk">{{ confirmModal.confirmText || "确定" }}</button>
        </div>
      </div>
    </div>

    <div v-if="roleModal.visible" class="mask" @click.self="closeRoleModal">
      <div class="modal modal-lg">
        <h3>{{ roleModal.mode === "create" ? "添加资源" : "编辑资源" }}</h3>
        <div class="modal-field-block">
          <label>资源类型</label>
          <div class="resource-type-selector">
            <label :class="{ active: roleForm.type === 'role' }"><input type="radio" v-model="roleForm.type" value="role" />角色</label>
            <label :class="{ active: roleForm.type === 'scene' }"><input type="radio" v-model="roleForm.type" value="scene" />场景</label>
            <label :class="{ active: roleForm.type === 'material' }"><input type="radio" v-model="roleForm.type" value="material" />素材</label>
          </div>
        </div>
        <input v-model.trim="roleForm.name" maxlength="20" :placeholder="namePlaceholder" />
        <input v-model.trim="roleForm.age" maxlength="20" :placeholder="agePlaceholder" />
        <input v-model.trim="roleForm.gender" maxlength="20" :placeholder="genderPlaceholder" />
        <input v-model.trim="roleForm.summary" maxlength="60" :placeholder="summaryPlaceholder" />
        <textarea v-model.trim="roleForm.description" class="modal-textarea" rows="8" :placeholder="descPlaceholder" />
        <div class="modal-actions">
          <button class="secondary-btn" @click="closeRoleModal">取消</button>
          <button class="main-btn modal-main-btn" @click="submitRoleModal">
            {{ roleModal.mode === "create" ? "确认添加" : "保存资源" }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="episodeModal.visible" class="mask" @click.self="closeEpisodeModal">
      <div class="modal modal-lg episode-modal">
        <h3>新增分集</h3>
        <p class="hint">每次仅新增 1 集，在列表末尾追加。标题可填；分集剧本/脚本正文不超过 1 万字。</p>
        <div class="modal-field-block">
          <label>分集标题</label>
          <input v-model.trim="episodeModal.title" maxlength="80" placeholder="可选，留空则自动为「第 N 集：新分集」" />
        </div>
        <div class="modal-field-block">
          <label>本分集内容</label>
          <textarea
            v-model.trim="episodeModal.content"
            class="script-editor episode-modal-script"
            maxlength="30000"
            placeholder="请输入本分集剧本或分镜脚本内容，不超过3万字"
            rows="10"
          ></textarea>
          <div class="episode-content-count">{{ (episodeModal.content || "").length }}/30000</div>
        </div>
        <div class="modal-actions">
          <button type="button" class="secondary-btn" @click="closeEpisodeModal">取消</button>
          <button type="button" class="main-btn modal-main-btn" @click="submitEpisodeModal">确定</button>
        </div>
      </div>
    </div>

    <ImagePreviewLayer
      :visible="storyboardCoverPreview.visible"
      :src="storyboardCoverPreview.src"
      :alt="storyboardCoverPreview.alt"
      @close="closeStoryboardCoverPreview"
      @img-error="onStoryboardCoverPreviewImgError"
    />

    <ImagePreviewLayer
      :visible="entityPreviewDialog.visible"
      :src="entityPreviewDialog.src"
      :alt="entityPreviewDialog.alt"
      @close="closeEntityPreviewDialog"
      @img-error="onEntityPreviewImgError"
    />

    <ScriptPreviewModal
      :visible="scriptPreviewModalVisible"
      :project-title="project.title"
      :style-line="scriptPreviewStyleLine"
      :ratio="project.ratio"
      :project-type="project.type"
      :video-prompt-language="projectVideoLang"
      :episode-line="scriptPreviewEpisodeLine"
      :script-type-label="scriptTypeLabel"
      :script-content="scriptContent"
      :reference-line="''"
      @close="closeScriptPreviewModal"
    />
  </div>
</template>

<script>
import { mockProjects } from "../mock/data";
import { userApi } from "../api/user";
import { setPollingTimeout, clearPollingTimeout, expBackoff, jitterDelay } from "../utils/pollHelpers";
import { kvStore } from "../utils/kvStore";
import { enqueue as enqueueOffline, startBackgroundDrainer, getPendingCount, drainOnce } from "../utils/offlineQueue";
import ScriptPreviewModal from "../components/ScriptPreviewModal.vue";
import ImagePreviewLayer from "../components/ImagePreviewLayer.vue";
import {
  HOME_PROJECTS_CACHE_KEY,
  LEGACY_SESSION_KEYS,
  projectStepCacheKey,
  step1StateStorageKey,
  step1TaskIdStorageKey
} from "../constants/projectCache";
import {
  projectTypeToVisualScopeKey,
  visualStyleFallbackIdsForProjectType
} from "../constants/visualStyleScopes";

/**
 * 项目创作三步（/project/setup）：步骤一剧本与异步任务、步骤二实体与出图任务、步骤三分集列表与缓存。
 * 真源：已登录以 projectDetail + saveProjectDraft 为准；未登录以 sessionStorage 兜底。勿在无关路径重复写 meta。
 */
export default {
  name: "ProjectSetupPage",
  components: {
    ScriptPreviewModal,
    ImagePreviewLayer
  },
  data() {
    const projectId = Number(this.$route.query.id || 0);
    const initialStep = Math.max(1, Math.min(3, Number(this.$route.query.step || 1)));
    const cacheProjects = JSON.parse(sessionStorage.getItem(HOME_PROJECTS_CACHE_KEY) || "[]");
    const allProjects = cacheProjects.length ? cacheProjects : mockProjects;
    const fallback = allProjects[0];
    const found = allProjects.find(item => item.id === projectId) || fallback;
    const project = {
      ...found,
      title: found.title || "",
      ratio: found.ratio || "16:9",
      type: found.type || "剧情演绎",
      videoPromptLanguage: found.videoPromptLanguage === "en" ? "en" : "zh"
    };
    return {
      project,
      videoLangOptions: [
        { label: "中文", value: "zh" },
        { label: "English", value: "en" }
      ],
      ratioOptions: [
        { label: "16:9", value: "16:9" },
        { label: "9:16", value: "9:16" }
      ],
      typeOptions: [
        { label: "剧情演绎", value: "剧情演绎", desc: "以角色对白和剧情为主" },
        { label: "旁白解说", value: "旁白解说", desc: "以旁白叙述为主" }
      ],
      activeStep: initialStep,
      // 注意：activeScriptType 存储英文值，便于后续入库；界面展示使用 label（中文）
      activeScriptType: "upload_script",
      scriptTypes: [
        { value: "upload_script", label: "上传我的剧本" },
        { value: "ai_generate_script_agent", label: "AI生成剧本（agent模式）" }
      ],
      scriptContent: "",
      /** 步骤一剧本原文统一上限 30000 字（上传剧本与 AI 创意模式同限） */
      step1ScriptMaxChars: 30000,
      /** 预设集数：智能 / 自定义 */
      episodeCountMode: "smart",
      customEpisodeCount: 5,
      /** 与后端 AI_STEP1_SMART_EPISODE_* / AI_STEP1_CUSTOM_EPISODE_MAX 对齐（创建任务接口可刷新） */
      step1EpisodeSmartDefault: 20,
      step1EpisodeSmartMin: 8,
      step1EpisodeSmartMax: 20,
      step1EpisodeCustomMax: 20,
      steps: [
        { id: 1, label: "1 基础设定" },
        { id: 2, label: "2 场景角色" },
        { id: 3, label: "3 分集脚本" }
      ],
      activeStyle: null,
      activeRoleId: null,
      /** 步骤2 左侧资源类型：角色 / 场景 / 素材 */
      resourceSubTab: "role",
      /** 步骤2 左侧仅看未生成项（角色/场景/素材） */
      resourceFilterUngenerated: false,
      activeSceneId: null,
      activeMaterialId: null,
      sceneItems: [],
      materialItems: [],
      /** 步骤一「重新生成剧本」清库进行中 */
      step1Regenerating: false,
      /** 后台 textModels / imageModels 返回的 pointsPerCall，用于展示扣费 */
      textModelRows: [],
      imageModelRows: [],
      /** 字典拉取后的完整画风列表；步骤一卡片用 step1StyleOptions（按项目类型筛选） */
      allVisualStyleOptions: [],
      roles: [],
      storyboards: [],
      /** 步骤三批量提交视频任务（用于「一键全部分镜」） */
      batchSending: false,
      /** 步骤三分集列表视图：card | table */
      storyboardViewMode: "card",
      /** 重新生成剧本 / 开始生成 等高风险动作的确认弹窗 */
      confirmModal: {
        visible: false,
        title: "",
        message: "",
        confirmText: "确定",
        cancelText: "取消",
        onConfirm: null,
      },
      roleModal: {
        visible: false,
        mode: "create",
        targetId: null,
        type: "role"
      },
      roleForm: {
        type: "role",
        name: "",
        age: "",
        gender: "",
        summary: "",
        description: "",
      },
      storyboardCoverPreview: {
        visible: false,
        src: "",
        alt: "",
        item: null
      },
      entityPreviewDialog: {
        visible: false,
        src: "",
        alt: "",
        entity: null,
        field: "preview"
      },
      storyboardForm: {},
      episodeModal: {
        visible: false,
        title: "",
        content: ""
      },
      scriptPreviewModalVisible: false,
      /** 第一步「开始生成」是否已完成（与缓存同步） */
      step1Generated: false,
      step1Generating: false,
      step1ProgressMessage: "",
      step1Progress: 0,
      /** 步骤一异步任务子步骤 1~5（来自 getGenerateProjectStep1TaskStatus.step1SubStep） */
      step1SubStep: 0,
      /** 步骤一剧本字数缓存（脚本内容未变时直接复用上次结果，避免每次按键 O(n) 重算） */
      _scriptCharCountCache: null,
      /** 步骤二：单个生成 / 批量生成加载态 */
      resourceGenerating: false,
      /** 各子标签批量提交中（与 step2WatchList 分标签轮询状态独立） */
      batchGeneratingByTab: { role: false, scene: false, material: false },
      /** 步骤二：待轮询的实体预览任务（刷新后从服务端恢复的队列） */
      step2WatchList: [],
      _step2PollTimer: null,
      _step2PollInFlight: false,
      /** 步骤二：resume / 批量 / 单条轮询串行，避免双通道抢改 step2WatchList */
      _step2Serial: null,
      /** 切到后台 tab 时跳过轮询，回前台时立刻补一次 */
      _visibilityHandler: null,
      _hydratedProjectId: 0,
      /** 已保存后禁止切换剧本生成类型 */
      scriptTypeLocked: false,
      /** 与 fa_ai_model 对齐的默认编码（接口失败时兜底） */
      textModelCode: "",
      /** 空串：由 loadDefaultAiModels 按后台启用列表首项填充，避免写死已下线的 z-image 导致与 imageModels 列表对不上 */
      imageModelCode: "",
      videoModelCode: "doubao-seedance-2-0-260128",
      /** 已登录且 projectDetail 成功返回时为 true，用于避免仅用本地 session 覆盖服务端空数据 */
      _hydratedFromServer: false,
      /** 服务端草稿保存防抖 */
      _draftSaveTimer: null,
      /** 步骤一异步任务轮询 */
      _step1TaskId: 0,
      _step1PollTimer: null,
      /** 离线写入队列长度（用于 UI 提示条 / badge），由 refreshOfflineQueueBadge 异步刷新 */
      offlineQueuePending: 0,
      /** 分集重生任务队列：每集一条 job {taskId, episodeId, episodeNo, episodeTitle, status, progress, message, errorMessage, shotCount} */
      regenQueue: [],
      _regenPollTimer: null,
      /** 标记 POST 提交中（按钮 loading 状态） */
      regenSubmitting: false,
      /** regenQueue sessionStorage key（同一用户、项目维度：先按项目分桶，便于多项目切换不串） */
      _regenStorage: null
    };
  },
  async created() {
    await this.loadSetupDictionary();
    await this.loadDefaultAiModels();
    const token = localStorage.getItem("user_token");
    if (token) {
      await this.hydrateProjectFromApi();
    }
    // 必须放在 hydrateProjectFromApi 之后：_regenStorageKey 需要 project.id
    this.restoreRegenQueueFromSession();
    if (!token || !this._hydratedFromServer) {
      this.hydrateStep1FromCache();
      this.hydrateSteps23FromSessionOnly();
    }
    this.clampActiveStyleForProjectType();
    // 支持从 query.step 直接落位到指定步骤（例如从分镜编辑返回到第三步）
    if (this.activeStep > 1) {
      await this.ensureGeneratedData(this.activeStep);
    }
    this.resumeStep1TaskPollingIfNeeded();
    // 登录后从服务端恢复进行中的分集重生任务 → 启动轮询
    if (token) {
      this.resumeRegenQueueFromServer();
    } else {
      // 未登录：清空 sessionStorage 的过期任务，避免堆积
      this.persistRegenQueue();
    }
  },
  beforeDestroy() {
    if (this._step1PollTimer) {
      clearTimeout(this._step1PollTimer);
      this._step1PollTimer = null;
    }
    if (this._regenPollTimer) {
      clearTimeout(this._regenPollTimer);
      this._regenPollTimer = null;
    }
    this.clearStep2BackgroundPoll();
    this._step2Serial = Promise.resolve();
    if (typeof document !== 'undefined' && this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    if (typeof this._offlineDrainerStop === 'function') {
      try { this._offlineDrainerStop(); } catch (e) { /* ignore */ }
      this._offlineDrainerStop = null;
    }
  },
  mounted() {
    if (typeof document !== 'undefined') {
      this._visibilityHandler = () => {
        // 切回前台时立即补一次轮询（避免依赖后台定时）
        if (!document.hidden && this.step2WatchList.length && !this._step2PollTimer && !this._step2PollInFlight) {
          this.startStep2BackgroundPoll(2000);
        }
      };
      document.addEventListener('visibilitychange', this._visibilityHandler);
    }
    // 启动离线写入兜底 drainer：监听 online + 15s tick
    // 这里传入实际发起请求的函数（userApi 包装），避免循环依赖
    this._offlineDrainerStop = startBackgroundDrainer(async (path, body) => {
      const fn = userApi && userApi[path];
      if (typeof fn !== 'function') {
        throw new Error("未注册的离线写路径：" + path);
      }
      return fn(body);
    });
    // 启动后立即看一次（页面切换时可能带着未发出的写）
    void drainOnce(async (path, body) => {
      const fn = userApi && userApi[path];
      if (typeof fn !== 'function') {
        throw new Error("未注册的离线写路径：" + path);
      }
      return fn(body);
    });
    this.refreshOfflineQueueBadge();
  },
  watch: {
    "$route.query.id"() {
      this.hydrateProjectFromApi().then(async () => {
        if (!this._hydratedFromServer) {
          this.hydrateStep1FromCache();
          this.hydrateSteps23FromSessionOnly();
        }
        this.clampActiveStyleForProjectType();
        if (localStorage.getItem("user_token")) {
          await this.loadDefaultAiModels();
        }
        if (this.activeStep > 1) {
          await this.ensureGeneratedData(this.activeStep);
        }
        // 切换项目后：读新项目的 regen 快照 → 再用服务端对账
        this.regenQueue = [];
        this.restoreRegenQueueFromSession();
        if (localStorage.getItem("user_token")) {
          await this.resumeRegenQueueFromServer();
        }
      });
    }
  },
  computed: {
    hasUserToken() {
      try {
        return !!(typeof window !== "undefined" && window.localStorage && window.localStorage.getItem("user_token"));
      } catch (e) {
        return false;
      }
    },
    activeRegenCount() {
      const queue = this.regenQueue || [];
      return queue.filter(j => j.status !== 'success' && j.status !== 'failed' && j.status !== 'cancelled').length;
    },
    highestReachableStep() {
      if (!this.step1Locked) return 1;
      if (!this.step2Ready) return 2;
      return 3;
    },
    liveStepProgress() {
      const p = Number(this.step1Progress);
      if (Number.isFinite(p) && p > 0) {
        return Math.max(0, Math.min(100, Math.round(p)));
      }
      return this.step1Generating ? 12 : 0;
    },
    stepStatus() {
      return (id) => {
        if (id === 1) {
          if (this.step1Locked) return "done";
          if (this.step1Generating) return "running";
          return "todo";
        }
        if (id === 2) {
          if (this.step2Ready) return "done";
          if (this.activeStep >= 2) return "running";
          return "todo";
        }
        if (id === 3) {
          if (this.storyboards.length) return "done";
          if (this.activeStep >= 3) return "running";
          return "todo";
        }
        return "todo";
      };
    },
    projectVideoLang() {
      return this.project && this.project.videoPromptLanguage === "en" ? "en" : "zh";
    },
    currentRole() {
      return this.roles.find(item => item.id === this.activeRoleId) || null;
    },
    currentScene() {
      return this.sceneItems.find(item => item.id === this.activeSceneId) || null;
    },
    currentMaterial() {
      return this.materialItems.find(item => item.id === this.activeMaterialId) || null;
    },
    namePlaceholder() {
      const map = { role: "角色名", scene: "场景名", material: "素材名" };
      return map[this.roleForm.type] || "名称";
    },
    agePlaceholder() {
      const map = { role: "年龄", scene: "时间/年代", material: "规格/尺寸" };
      return map[this.roleForm.type] || "年龄";
    },
    genderPlaceholder() {
      const map = { role: "性别", scene: "季节/天气", material: "材质/类型" };
      return map[this.roleForm.type] || "性别";
    },
    summaryPlaceholder() {
      const map = { role: "角色摘要", scene: "场景摘要", material: "素材摘要" };
      return map[this.roleForm.type] || "摘要";
    },
    descPlaceholder() {
      const map = {
        role: "请输入角色描述",
        scene: "请输入场景描述",
        material: "请输入素材描述"
      };
      return map[this.roleForm.type] || "请输入描述";
    },
    currentResourceList() {
      if (this.resourceSubTab === "role") return this.roles;
      if (this.resourceSubTab === "scene") return this.sceneItems;
      if (this.resourceSubTab === "material") return this.materialItems;
      return [];
    },
    displayedResourceList() {
      const list = this.currentResourceList || [];
      if (!this.resourceFilterUngenerated) return list;
      return list.filter(item => !this.isResourceGenerated(item));
    },
    currentResourceUngeneratedCount() {
      const list = this.currentResourceList || [];
      return list.filter(item => !this.isResourceGenerated(item)).length;
    },
    currentResourceEntity() {
      if (this.resourceSubTab === "role") return this.currentRole;
      if (this.resourceSubTab === "scene") return this.currentScene;
      if (this.resourceSubTab === "material") return this.currentMaterial;
      return null;
    },
    roleHasPreviewImage() {
      if (!this.currentRole) return false;
      return !!String(this.currentRole.preview || "").trim();
    },
    entityHasPreviewImage() {
      const e = this.currentResourceEntity;
      if (!e) return false;
      return !!String(e.preview || "").trim();
    },
    resourceDetailTitle() {
      const m = { role: "角色设定", scene: "场景设定", material: "素材设定" };
      return m[this.resourceSubTab] || "设定";
    },
    previewPlaceholderText() {
      const m = {
        role: "暂无预览图，可点击「生成」按钮生成角色预览",
        scene: "暂无预览图，可点击「生成」按钮生成场景预览",
        material: "暂无预览图，可点击「生成」按钮生成素材预览"
      };
      return m[this.resourceSubTab] || "暂无预览图";
    },
    resourcePanelTitle() {
      const m = { role: "我的角色", scene: "我的场景", material: "我的素材" };
      return m[this.resourceSubTab] || "我的角色";
    },
    entityDescriptionPlaceholder() {
      if (this.resourceSubTab === "role") {
        return "请填写角色生图描述：身高、体型、面部特征、发型、眼睛、服装、显著特征；并明确“全身图、白色底图、无任何文字”。";
      }
      if (this.resourceSubTab === "scene") {
        return "请填写场景生图描述：空间结构、材质、色彩、光源、关键陈设；并明确“360°全景图、白色底图、无任何文字、无人”。";
      }
      const t = String((this.project && this.project.type) || "").trim();
      if (t === "商品讲解" || t.indexOf("商品") !== -1) {
        return "请填写商品/包装生图描述：主体形态、材质、色彩、细节与品牌留白；并明确“完整正面图、白色底图、无任何文字”。";
      }
      return "请填写素材生图描述：材质、尺寸、颜色、形状、细节、状态；并明确“完整正面图、白色底图、无任何文字”。";
    },
    isFromEdit() {
      const q = this.$route.query;
      return q.mode === "edit" || q.from === "edit";
    },
    step1Locked() {
      return !!(this.step1Generated || this.scriptTypeLocked);
    },
    /** 步骤一画面风格卡片：短剧全量；商品/MG 优先字典 scopes，无 scopes 时与兜底 id 白名单求交（顺序与字典一致） */
    step1StyleOptions() {
      const all = Array.isArray(this.allVisualStyleOptions) ? this.allVisualStyleOptions : [];
      if (!all.length) return [];
      const scopeKey = projectTypeToVisualScopeKey(this.project && this.project.type);
      if (scopeKey === "short") return all;

      const fallbackIds = visualStyleFallbackIdsForProjectType(this.project && this.project.type);
      const fbSet =
        Array.isArray(fallbackIds) && fallbackIds.length ? new Set(fallbackIds.map(Number)) : null;

      const out = [];
      const seen = new Set();
      for (const s of all) {
        if (!s) continue;
        const id = Number(s.id);
        if (!id || seen.has(id)) continue;
        const sc = s.scopes;
        let ok = false;
        if (Array.isArray(sc) && sc.length) {
          if (sc.includes("all") || sc.includes(scopeKey)) ok = true;
        } else if (fbSet && fbSet.has(id)) {
          ok = true;
        }
        if (ok) {
          seen.add(id);
          out.push(s);
        }
      }

      if (!out.length && fbSet) {
        for (const s of all) {
          if (!s) continue;
          const id = Number(s.id);
          if (fbSet.has(id)) out.push(s);
        }
      }
      return out.length ? out : all;
    },
    activeStyleLabel() {
      const all = Array.isArray(this.allVisualStyleOptions) ? this.allVisualStyleOptions : [];
      const o = all.find(s => Number(s.id) === Number(this.activeStyle));
      return o ? String(o.name || "") : "";
    },
    step1StyleReferences() {
      const all = Array.isArray(this.allVisualStyleOptions) ? this.allVisualStyleOptions : [];
      const o = all.find(s => Number(s.id) === Number(this.activeStyle));
      if (!o) return [];
      const refs = Array.isArray(o.references) ? o.references : [];
      return refs
        .filter(r => r && (r.cover || r.name))
        .slice(0, 8)
        .map(r => ({
          name: String(r.name || r.title || "参考"),
          cover: String(r.cover || r.image || r.url || "").trim()
        }));
    },
    scriptPreviewStyleLine() {
      const o = this.allVisualStyleOptions.find(s => Number(s.id) === Number(this.activeStyle));
      const name = o ? o.name : "未选择";
      const def = "3D，CG动画，东方奇幻渲染（示例标签，以所选画面风格为准）";
      const extra = o && String(o.description || "").trim() ? String(o.description).trim() : def;
      return `${name}，${extra}`;
    },
    scriptPreviewEpisodeLine() {
      if (this.episodeCountMode === "custom") {
        return `自定义 ${this.customEpisodeCount} 集（上限 ${this.step1EpisodeCustomMax} 集，与生成服务一致）`;
      }
      return `智能集数（约 ${this.step1EpisodeSmartDefault} 集，随机 ${this.step1EpisodeSmartMin}～${this.step1EpisodeSmartMax} 集）`;
    },
    scriptTypeLabel() {
      const t = this.scriptTypes.find(x => x.value === this.activeScriptType);
      return t ? t.label : "—";
    },
    scriptPlaceholder() {
      if (this.activeScriptType === "upload_script") {
        return `请粘贴剧本或小说正文，单次不超过 ${this.step1ScriptMaxChars} 字`;
      }
      if (this.activeScriptType === "ai_generate_script_agent") {
        return "输入一句话或简短梗概即可（如：创作一部玄幻漫剧），由 AI 扩写为完整剧本；也可写得更详细。";
      }
      return "请输入内容";
    },
    /** 与后端 mb_strlen(UTF-8) 对齐：扩展平面字符按单个码点计
     *  优化：使用 \u 处理代理对，避免每次按键都 Array.from 大字符串（O(n) per keystroke）。
     *  对上限 30000 字场景：CJK BMP 单字计 1，Emoji 代理对计 1（与 mb_strlen 一致）。
     */
    scriptContentCharCount() {
      const s = String(this.scriptContent || "");
      let n = 0;
      // 配合 bmpSurrogateCountCache 用：每个 cache key = 一段 s 切片，但 s 是单个 textarea 内容、整字符串缓存足够
      // 这里走缓存路径：如果字符串 hash 没变（textarea 自身变了但内容相同），直接复用
      const cached = this._scriptCharCountCache;
      if (cached && cached.s === s) {
        return cached.n;
      }
      // 快速 UTF-16 codepoint 计数（与 Array.from 等价但常数项小约 5 倍）：
      //   - 跳过所有 BMP 字符（len 1）
      //   - 跳过孤立 surrogate（count 时视为 1）
      //   - 遇到 surrogate pair 时只算 1（与 Array.from 一致）
      for (let i = 0, len = s.length; i < len; i++) {
        const code = s.charCodeAt(i);
        if (code >= 0xd800 && code <= 0xdbff) {
          // 高代理：跳过配对的低代理，避免重复计 2
          if (i + 1 < len) {
            const next = s.charCodeAt(i + 1);
            if (next >= 0xdc00 && next <= 0xdfff) {
              i++;
            }
          }
        }
        n++;
      }
      this._scriptCharCountCache = { s, n };
      return n;
    },
    scriptContentNearLimit() {
      if (this.step1Locked) return false;
      const max = Number(this.step1ScriptMaxChars) || 0;
      if (max <= 0) return false;
      return this.scriptContentCharCount >= Math.floor(max * 0.85) && this.scriptContentCharCount <= max;
    },
    scriptContentOverLimit() {
      if (this.step1Locked) return false;
      return this.scriptContentCharCount > this.step1ScriptMaxChars;
    },
    step2AllEntities() {
      const a = [];
      if (Array.isArray(this.roles)) a.push(...this.roles);
      if (Array.isArray(this.sceneItems)) a.push(...this.sceneItems);
      if (Array.isArray(this.materialItems)) a.push(...this.materialItems);
      return a.filter(Boolean);
    },
    step2MissingPreviewCount() {
      const list = this.step2AllEntities;
      let n = 0;
      for (const it of list) {
        if (!this.hasPreviewImage(this.getResourceThumb(it))) n++;
      }
      return n;
    },
    step2Ready() {
      const total = this.step2AllEntities.length;
      if (total <= 0) return false;
      return this.step2MissingPreviewCount === 0;
    },
    /** 当前所选文字模型单次步骤一扣费（含会员折扣，与后台一致） */
    step1PointsValue() {
      const code = String(this.textModelCode || "").trim();
      const rows = Array.isArray(this.textModelRows) ? this.textModelRows : [];
      const hit = rows.find(x => String(x.code || "") === code);
      const p = hit && typeof hit.pointsPerCall === "number" ? hit.pointsPerCall : 0;
      return Number.isFinite(p) && p > 0 ? p : 0;
    },
    step1PointsLabel() {
      return this.step1PointsValue > 0 ? `${this.step1PointsValue} 积分` : "未配置扣费";
    },
    step1GenerateDisabled() {
      if (this.step1Generating) return true;
      if (this.activeScriptType === "upload_script" && this.scriptContentOverLimit) return true;
      if (this.step1PointsValue <= 0) return true;
      return false;
    },
    step1RegenerateDisabled() {
      if (this.step1Regenerating || this.step1Generating) return true;
      if (this.activeScriptType === "upload_script" && this.scriptContentOverLimit) return true;
      if (this.step1PointsValue <= 0) return true;
      return false;
    },
    step2SinglePointsValue() {
      const code = String(this.imageModelCode || "").trim();
      const rows = Array.isArray(this.imageModelRows) ? this.imageModelRows : [];
      let hit = rows.find(x => String(x.code || "") === code);
      // 与后端 resolveUsableImageModel 一致：当前 code 不在启用列表时按 weigh/id 首项计费展示
      if (!hit && rows.length) {
        hit = rows[0];
      }
      const p = hit && typeof hit.pointsPerCall === "number" ? hit.pointsPerCall : 0;
      return Number.isFinite(p) && p > 0 ? p : 0;
    },
    step2SinglePointsLabel() {
      return this.step2SinglePointsValue > 0 ? `${this.step2SinglePointsValue} 积分/张` : "未配置扣费";
    },
    batchImagePointsSummary() {
      const p = this.step2SinglePointsValue;
      if (p <= 0) return "未配置扣费";
      const tab = this.resourceSubTab;
      const list =
        tab === "role" ? this.roles : tab === "scene" ? this.sceneItems : this.materialItems;
      if (!Array.isArray(list) || !list.length) return `${p} 积分/张`;
      const n = list.filter(it => it && it.id && !this.hasPreviewImage(this.getResourceThumb(it))).length;
      if (n <= 0) return ` ${p} 积分/张`;
      //return ` ${n * p} 积分（${n} 张×${p}）`;
      return ` ${n * p} 积分`;
    },
    isCurrentTabBatchBusy() {
      return this.isTabBatchBusy(this.resourceSubTab);
    },
    currentTabStep2WatchCount() {
      const ids = this.step2EntityIdSetForTab(this.resourceSubTab);
      return (this.step2WatchList || []).filter(j => ids.has(Number(j.entityId))).length;
    },
    step2ImageWorkLocked() {
      // 单图重新生成按钮的锁定：
      // 1) 本次单图任务在跑（resourceGenerating）
      // 2) 当前子标签的批量提交在跑（batchGeneratingByTab[tab]）
      // 3) 当前选中的实体本身有未完成的轮询任务（避免对同一实体重复触发）
      const tab = this.resourceSubTab;
      const batchBusy = tab === "role" || tab === "scene" || tab === "material"
        ? !!this.batchGeneratingByTab[tab]
        : false;
      const e = this.currentResourceEntity;
      const currentEntityBusy = e && e.id
        ? (this.step2WatchList || []).some(j => Number(j.entityId) === Number(e.id) && j.busy)
        : false;
      return this.resourceGenerating || batchBusy || currentEntityBusy;
    },
    step2CurrentEntityBusyLabel() {
      const e = this.currentResourceEntity;
      const inQueue =
        e &&
        e.id &&
        this.step2WatchList.some(j => Number(j.entityId) === Number(e.id) && j.busy);
      if (this.resourceGenerating || inQueue) {
        return this.entityHasPreviewImage ? "重新生成中…" : "生成中…";
      }
      return this.entityHasPreviewImage ? "重新生成" : "生成";
    },
    step2QueueHintText() {
      const tab = this.resourceSubTab;
      const typeLabels = { role: "角色", scene: "场景", material: "素材" };
      const ids = this.step2EntityIdSetForTab(tab);
      const jobs = (this.step2WatchList || []).filter(j => ids.has(Number(j.entityId)));
      const n = jobs.length;
      if (!n) return "";
      const names = jobs
        .map(j => (j.entityName && String(j.entityName).trim()) || `#${j.entityId}`)
        .slice(0, 5);
      const tail = n > 5 ? `等 ${n} 项` : `共 ${n} 项`;
      const label = typeLabels[tab] || "资源";
      return `${label}图片生成中：${names.join("、")}（${tail}）。`;
    }
  },
  methods: {
    onImgError(item, field) {
      if (!item || !field) return;
      try {
        // 将失败图片置空，使 isPlaceholderAsset 命中并回退到占位块
        item[field] = "";
      } catch (e) {
        /* ignore */
      }
    },
    windowAlert(message) {
      window.alert(message);
    },
    notifyPointsChanged() {
      if (this.$root && typeof this.$root.$emit === "function") {
        this.$root.$emit("points-changed");
      }
    },
    /**
     * 刷新离线写入队列长度到组件状态（用于 UI badge / 提示条）。
     * 失败时静默忽略。
     */
    async refreshOfflineQueueBadge() {
      try {
        const n = await getPendingCount();
        if (n !== this.offlineQueuePending) {
          this.offlineQueuePending = n;
        }
      } catch (e) {
        /* ignore */
      }
    },
    /** 用户主动放弃离线重试（清空整队列）。 */
    async clearOfflineQueue() {
      try {
        const { clearQueue } = await import("../utils/offlineQueue");
        await clearQueue();
        this.offlineQueuePending = 0;
        if (this.$message && this.$message.success) {
          this.$message.success("已清空离线写入队列");
        }
      } catch (e) {
        /* ignore */
      }
    },
    /** 用户主动重试一次离线队列（手动 drain）。 */
    async retryOfflineQueueNow() {
      try {
        await drainOnce(async (path, body) => {
          const fn = userApi && userApi[path];
          if (typeof fn !== 'function') {
            throw new Error("未注册的离线写路径：" + path);
          }
          return fn(body);
        });
        await this.refreshOfflineQueueBadge();
        if (this.$message && this.$message.success) {
          this.$message.success("已重试离线队列");
        }
      } catch (e) {
        if (this.$message && this.$message.error) {
          this.$message.error("重试失败：" + ((e && e.message) || '未知错误'));
        }
      }
    },
    async loadSetupDictionary() {
      try {
        const d = await userApi.setupDictionary();
        if (!d || typeof d !== "object") return;
        if (Array.isArray(d.aspectRatio) && d.aspectRatio.length) {
          this.ratioOptions = d.aspectRatio.map(x => ({
            label: x.label || x.value,
            value: x.value
          }));
        }
        if (Array.isArray(d.projectType) && d.projectType.length) {
          this.typeOptions = d.projectType.map(x => ({
            label: x.label || x.value,
            value: x.value,
            desc: x.desc || ""
          }));
        }
        if (Array.isArray(d.visualStyle) && d.visualStyle.length) {
          this.allVisualStyleOptions = d.visualStyle
            .map(x => ({
              id: Number(x.id),
              name: x.name || "",
              cover: x.cover || "",
              description: x.description || "",
              scopes: Array.isArray(x.scopes) && x.scopes.length ? x.scopes.map(String) : undefined
            }))
            .filter(s => Number.isFinite(s.id) && s.id > 0);
          this.clampActiveStyleForProjectType();
        }
        if (Array.isArray(d.videoPromptLanguage) && d.videoPromptLanguage.length) {
          this.videoLangOptions = d.videoPromptLanguage.map(x => ({
            label: x.label || x.value,
            value: x.value === "en" ? "en" : "zh"
          }));
        }
        const rv = String(this.project.ratio || "");
        if (this.ratioOptions.length && !this.ratioOptions.some(o => o.value === rv)) {
          this.project.ratio = this.ratioOptions[0].value;
        }
        const tv = String(this.project.type || "");
        if (this.typeOptions.length && !this.typeOptions.some(o => o.value === tv)) {
          this.project.type = this.typeOptions[0].value;
        }
      } catch (e) {
        /* 字典加载失败时保持空列表，由用户刷新或检查接口 */
      }
    },
    setActiveStyle(id) {
      this.activeStyle = id;
      this.persistStep1State();
    },
    setProjectRatio(value) {
      this.project.ratio = value;
      this.persistProjectToCache();
    },
    setProjectType(value) {
      this.project.type = value;
      this.applyProjectTypeStep1Defaults(value);
      this.clampActiveStyleForProjectType();
      this.persistProjectToCache();
    },
    /** 当前画风若不在「该类型可选列表」内，则回退到列表首项（切换类型时触发） */
    clampActiveStyleForProjectType() {
      const list = this.step1StyleOptions;
      if (!Array.isArray(list) || !list.length) return;
      const cur = Number(this.activeStyle);
      const ids = new Set(list.map(s => Number(s.id)));
      if (!ids.has(cur)) {
        this.activeStyle = Number(list[0].id);
        this.persistStep1State();
      }
    },
    applyProjectTypeStep1Defaults(type) {
      const t = String(type || "");
      if (t === "商品讲解") {
        this.project.ratio = "16:9";
      }
      if (t === "MG动画") {
        this.project.ratio = "16:9";
      }
    },
    async setProjectVideoLang(value) {
      const v = value === "en" ? "en" : "zh";
      this.$set(this.project, "videoPromptLanguage", v);
      this.persistProjectToCache();
      const id = this.project && this.project.id;
      if (!id || !localStorage.getItem("user_token")) return;
      try {
        await userApi.updateProject({
          id,
          title: this.project.title || "",
          ratio: this.project.ratio || "16:9",
          type: this.project.type || "剧情演绎",
          cover: this.project.cover || "",
          videoPromptLanguage: v
        });
      } catch (e) {
        /* 离线或接口失败：本地已切换，刷新后可能回退 */
      }
    },
    clampCustomEpisodeCount() {
      let n = Number(this.customEpisodeCount);
      if (Number.isNaN(n) || n < 1) n = 1;
      const cap = Math.max(1, Math.min(50, Number(this.step1EpisodeCustomMax) || 50));
      if (n > cap) n = cap;
      this.customEpisodeCount = n;
    },
    applyStep1EpisodeLimitsFromApi(res) {
      if (!res || typeof res !== "object") return;
      const lim = res.episodeCountLimits;
      if (lim && typeof lim === "object") {
        const mx = Number(lim.customMax);
        const sm = Number(lim.smartDefault);
        const smin = Number(lim.smartMin);
        const smax = Number(lim.smartMax);
        if (Number.isFinite(mx) && mx >= 1) this.step1EpisodeCustomMax = Math.min(50, Math.floor(mx));
        if (Number.isFinite(sm) && sm >= 1) this.step1EpisodeSmartDefault = Math.min(50, Math.floor(sm));
        if (Number.isFinite(smin) && smin >= 1) this.step1EpisodeSmartMin = Math.min(50, Math.floor(smin));
        if (Number.isFinite(smax) && smax >= 1) this.step1EpisodeSmartMax = Math.min(50, Math.floor(smax));
        if (this.step1EpisodeSmartMin > this.step1EpisodeSmartMax) {
          this.step1EpisodeSmartMax = this.step1EpisodeSmartMin;
        }
      }
    },
    hydrateStep1FromCache() {
      const id = this.project && this.project.id;
      if (!id) return;
      const raw = sessionStorage.getItem(step1StateStorageKey(id));
      if (!raw) return;
      try {
        const s = JSON.parse(raw);
        if (s.activeScriptType) this.activeScriptType = s.activeScriptType;
        if (typeof s.scriptContent === "string") this.scriptContent = s.scriptContent;
        if (s.episodeCountMode === "smart" || s.episodeCountMode === "custom") {
          this.episodeCountMode = s.episodeCountMode;
        }
        if (typeof s.customEpisodeCount === "number") this.customEpisodeCount = s.customEpisodeCount;
        this.clampCustomEpisodeCount();
        if (typeof s.activeStyle === "number") this.activeStyle = s.activeStyle;
        if (typeof s.scriptSaved === "boolean") this.scriptTypeLocked = s.scriptSaved;
        else if (typeof s.scriptSaved === "number" || typeof s.scriptSaved === "string") {
          this.scriptTypeLocked = Number(s.scriptSaved) === 1;
        } else this.scriptTypeLocked = !!s.scriptSaved;
        if (typeof s.step1Generated === "boolean") this.step1Generated = s.step1Generated;
        else if (typeof s.step1Generated === "number" || typeof s.step1Generated === "string") {
          this.step1Generated = Number(s.step1Generated) === 1;
        } else this.step1Generated = !!s.step1Generated;
      } catch (e) {
        /* ignore */
      }
    },
    persistStep1State({ scriptSaved } = {}) {
      const id = this.project && this.project.id;
      if (!id) return;
      const saved = scriptSaved !== undefined ? scriptSaved : this.scriptTypeLocked;
      const payload = {
        scriptSaved: saved,
        step1Generated: this.step1Generated,
        activeScriptType: this.activeScriptType,
        scriptContent: this.scriptContent,
        episodeCountMode: this.episodeCountMode,
        customEpisodeCount: this.customEpisodeCount,
        activeStyle: this.activeStyle
      };
      sessionStorage.setItem(step1StateStorageKey(id), JSON.stringify(payload));
      this.scheduleSaveProjectDraft();
    },
    step1TaskKey() {
      return step1TaskIdStorageKey(this.project && this.project.id);
    },
    setStep1TaskId(taskId) {
      const id = Number(taskId || 0);
      this._step1TaskId = id;
      if (id > 0) sessionStorage.setItem(this.step1TaskKey(), String(id));
      else sessionStorage.removeItem(this.step1TaskKey());
    },
    readStep1TaskIdFromSession() {
      const raw = sessionStorage.getItem(this.step1TaskKey());
      const id = Number(raw || 0);
      return Number.isFinite(id) ? id : 0;
    },
    resumeStep1TaskPollingIfNeeded() {
      if (!localStorage.getItem("user_token")) return;
      if (this.step1Generated) return;
      if (this.step1Generating) return;
      const taskId = this.readStep1TaskIdFromSession();
      if (taskId > 0) {
        this.startStep1TaskPolling(taskId, 5000);
      }
    },
    buildStep1MetaForApi() {
      return {
        activeScriptType: this.activeScriptType,
        scriptContent: this.scriptContent,
        episodeCountMode: this.episodeCountMode,
        customEpisodeCount: this.customEpisodeCount,
        activeStyle: this.activeStyle,
        step1Generated: this.step1Generated,
        scriptSaved: this.scriptTypeLocked
      };
    },
    /** 完整 meta_json（三步 + 模型编码），与后端 normalizeProjectMeta 对齐 */
    buildFullProjectMeta() {
      return {
        version: 1,
        step1: this.buildStep1MetaForApi(),
        step2: {
          roles: this.roles,
          scenes: this.sceneItems,
          materials: this.materialItems,
          productEntityIds: []
        },
        step3: {
          storyboards: this.storyboards
        },
        models: {
          textModelCode: this.textModelCode,
          imageModelCode: this.imageModelCode,
          videoModelCode: this.videoModelCode
        },
        lastActiveStep: this.activeStep
      };
    },
    ssKey(kind) {
      return projectStepCacheKey(kind, this.project && this.project.id);
    },
    readSessionList(kind, legacyKey) {
      let raw = sessionStorage.getItem(this.ssKey(kind));
      if (!raw && legacyKey) {
        raw = sessionStorage.getItem(legacyKey);
      }
      try {
        const a = JSON.parse(raw || "[]");
        return Array.isArray(a) ? a : [];
      } catch (e) {
        return [];
      }
    },
    hydrateSteps23FromSessionOnly() {
      const r = this.readSessionList("roles", LEGACY_SESSION_KEYS.roles);
      const s = this.readSessionList("scenes", LEGACY_SESSION_KEYS.scenes);
      const m = this.readSessionList("materials", LEGACY_SESSION_KEYS.materials);
      const sb = this.readSessionList("storyboards", LEGACY_SESSION_KEYS.storyboards);
      if (r.length) {
        this.roles = r;
        this.activeRoleId = this.roles[0] ? this.roles[0].id : null;
      }
      if (s.length) {
        this.sceneItems = s;
        this.activeSceneId = this.sceneItems[0] ? this.sceneItems[0].id : null;
      }
      if (m.length) {
        this.materialItems = m;
        this.activeMaterialId = this.materialItems[0] ? this.materialItems[0].id : null;
      }
      this.mergeLegacyProductSessionIntoMaterials(this.readSessionList("products", null));
      if (sb.length) {
        this.storyboards = sb;
      }
    },
    async loadDefaultAiModels() {
      try {
        const [t, i] = await Promise.all([
          userApi.textModels().catch(() => []),
          userApi.imageModels().catch(() => [])
        ]);
        this.textModelRows = Array.isArray(t) ? t : [];
        this.imageModelRows = Array.isArray(i) ? i : [];
        const curText = String(this.textModelCode || "").trim();
        const curImg = String(this.imageModelCode || "").trim();
        const textCodes = new Set(
          this.textModelRows.map(r => String(r.code || "").trim()).filter(Boolean)
        );
        const imgCodes = new Set(
          this.imageModelRows.map(r => String(r.code || "").trim()).filter(Boolean)
        );
        if (this.textModelRows.length && this.textModelRows[0].code && (!curText || !textCodes.has(curText))) {
          this.textModelCode = this.textModelRows[0].code;
        }
        if (this.imageModelRows.length && this.imageModelRows[0].code && (!curImg || !imgCodes.has(curImg))) {
          this.imageModelCode = this.imageModelRows[0].code;
        }
      } catch (e) {
        /* 使用 data 默认值 */
      }
    },
    syncSessionCachesFromState() {
      const id = this.project && this.project.id;
      if (!id) return;
      const flush = () => {
        try {
          sessionStorage.setItem(this.ssKey("roles"), JSON.stringify(this.roles));
          sessionStorage.setItem(this.ssKey("scenes"), JSON.stringify(this.sceneItems));
          sessionStorage.setItem(this.ssKey("materials"), JSON.stringify(this.materialItems));
          sessionStorage.setItem(this.ssKey("storyboards"), JSON.stringify(this.storyboards));
          const payload = {
            scriptSaved: this.scriptTypeLocked,
            step1Generated: this.step1Generated,
            activeScriptType: this.activeScriptType,
            scriptContent: this.scriptContent,
            episodeCountMode: this.episodeCountMode,
            customEpisodeCount: this.customEpisodeCount,
            activeStyle: this.activeStyle
          };
          sessionStorage.setItem(step1StateStorageKey(id), JSON.stringify(payload));
        } catch (e) {
          /* ignore quota / private mode */
        }
      };
      if (typeof requestIdleCallback === "function") {
        requestIdleCallback(() => flush(), { timeout: 900 });
      } else {
        setTimeout(flush, 0);
      }
    },
    /** projectDetail 顶层 storyboards 可能不含 shots（与 episodes 拆分传输），合并后再写入草稿 */
    mergeStoryboardsWithEpisodesShots(storyboards, episodes) {
      if (!Array.isArray(storyboards)) return [];
      const eps = Array.isArray(episodes) ? episodes : [];
      const byEp = new Map(eps.map(ep => [Number((ep && ep.id) || 0), ep]));
      return storyboards.map(sb => {
        const sid = Number((sb && sb.id) || 0);
        if (Array.isArray(sb.shots) && sb.shots.length) return sb;
        const ep = byEp.get(sid);
        const shots = ep && Array.isArray(ep.shots) ? ep.shots : [];
        return { ...sb, shots };
      });
    },
    /** 接口 materials 与历史 productEntityIds 标记项统一归入素材列表 */
    applyStep2MaterialsSplitFromRow(row, m) {
      const step2 = m.step2 && typeof m.step2 === "object" ? m.step2 : {};
      const mat = Array.isArray(row.materials)
        ? row.materials
        : Array.isArray(step2.materials)
          ? step2.materials
          : [];
      this.materialItems = (mat || []).filter(it => it && typeof it === "object");
      this.activeMaterialId = this.materialItems[0] ? this.materialItems[0].id : null;
    },
    /** 旧 session「products」缓存合并进 materialItems（按 id 去重） */
    mergeLegacyProductSessionIntoMaterials(extraList) {
      const list = Array.isArray(extraList) ? extraList : [];
      if (!list.length) return;
      const seen = new Set(
        (this.materialItems || []).map(it => Number((it && it.id) || 0)).filter(n => n > 0)
      );
      list.forEach(it => {
        if (!it || typeof it !== "object") return;
        const id = Number(it.id);
        if (id > 0 && seen.has(id)) return;
        if (id > 0) seen.add(id);
        this.materialItems.push(it);
      });
      if (!this.activeMaterialId && this.materialItems[0]) {
        this.activeMaterialId = this.materialItems[0].id;
      }
    },
    scheduleSaveProjectDraft() {
      const id = this.project && this.project.id;
      if (!id || !localStorage.getItem("user_token")) return;
      if (!this._hydratedFromServer) return;
      clearTimeout(this._draftSaveTimer);
      this._draftSaveTimer = setTimeout(() => this.flushProjectDraftToApi(), 1500);
    },
    async flushProjectDraftToApi() {
      const id = this.project && this.project.id;
      if (!id || !localStorage.getItem("user_token")) return;
      if (!this._hydratedFromServer) return;
      if (this.activeStep > 1 && !this.step1Generated) return;
      const metaJson = JSON.stringify(this.buildFullProjectMeta());
      try {
        await userApi.saveProjectDraft({
          id,
          metaJson
        });
        // 镜像到 IDB：断网 / 后端 5xx 时前端仍能拿到最近一次成功的草稿（离线兜底）
        const cacheKey = "draft:meta:" + Number(id);
        kvStore.set(
          cacheKey,
          { id: Number(id), metaJson, updateTime: Date.now() },
          (serialized) => {
            try { localStorage.setItem(cacheKey, serialized); } catch (e) { /* ignore */ }
          }
        );
      } catch (e) {
        // 网络异常 / 后端 5xx：把写操作入队离线，等 online 事件触发 drain
        try {
          await enqueueOffline("saveProjectDraft", { id, metaJson }, e && e.message ? e.message : "网络异常");
          this.refreshOfflineQueueBadge();
        } catch (e2) {
          /* ignore: 兜底失败，draft 仍在 IDB 里，丢失部分场景由下一次正常 save 覆盖 */
        }
      }
    },
    mergeProjectIntoHomeCache() {
      const id = this.project && this.project.id;
      if (!id) return;
      let list = [];
      try {
        list = JSON.parse(sessionStorage.getItem(HOME_PROJECTS_CACHE_KEY) || "[]");
      } catch (e) {
        list = [];
      }
      const idx = list.findIndex(p => p.id === id);
      const entry = {
        id,
        title: this.project.title || "",
        ratio: this.project.ratio,
        type: this.project.type,
        videoPromptLanguage: this.project.videoPromptLanguage === "en" ? "en" : "zh",
        cover: this.project.cover || "",
        updateText: idx !== -1 && list[idx].updateText ? list[idx].updateText : ""
      };
      if (idx !== -1) list[idx] = { ...list[idx], ...entry };
      else list.unshift(entry);
      sessionStorage.setItem(HOME_PROJECTS_CACHE_KEY, JSON.stringify(list));
    },
    async hydrateProjectFromApi() {
      const pid = Number(this.$route.query.id || 0);
        if (!pid || !localStorage.getItem("user_token")) {
        this._hydratedFromServer = false;
        this._hydratedProjectId = 0;
        return;
      }
      this.clearStep2BackgroundPoll();
      this._step2Serial = Promise.resolve();
      try {
        const row = await userApi.projectDetail(pid);
        if (!row || typeof row !== "object") {
          this._hydratedFromServer = false;
          return;
        }
        this._hydratedFromServer = true;
        this._hydratedProjectId = Number(row.id) || pid;
        this.project.id = Number(row.id) || pid;
        if (row.title) this.project.title = row.title;
        if (row.ratio) this.project.ratio = row.ratio;
        if (row.type) this.project.type = row.type;
        this.$set(
          this.project,
          "videoPromptLanguage",
          row.videoPromptLanguage === "en" ? "en" : "zh"
        );
        if (row.cover !== undefined && row.cover !== null) this.$set(this.project, "cover", row.cover);
        const vsc = Number(row.visualStyleCode);
        if (Number.isFinite(vsc) && vsc > 0) this.activeStyle = vsc;
        const m = row.meta && typeof row.meta === "object" ? row.meta : {};
        const s1 = m.step1 && typeof m.step1 === "object" ? m.step1 : m;
        if (s1.activeScriptType) this.activeScriptType = s1.activeScriptType;
        if (typeof row.scriptContent === "string") {
          this.scriptContent = row.scriptContent;
        } else if (typeof s1.scriptContent === "string") {
          this.scriptContent = s1.scriptContent;
        }
        if (s1.episodeCountMode === "smart" || s1.episodeCountMode === "custom") {
          this.episodeCountMode = s1.episodeCountMode;
        }
        if (typeof s1.customEpisodeCount === "number") this.customEpisodeCount = s1.customEpisodeCount;
        this.clampCustomEpisodeCount();
        if (typeof s1.activeStyle === "number" && s1.activeStyle > 0) this.activeStyle = s1.activeStyle;
        if (typeof s1.step1Generated === "boolean") {
          this.step1Generated = s1.step1Generated;
        } else if (typeof s1.step1Generated === "number" || typeof s1.step1Generated === "string") {
          this.step1Generated = Number(s1.step1Generated) === 1;
        }
        if (typeof s1.scriptSaved === "boolean") {
          this.scriptTypeLocked = s1.scriptSaved;
        } else if (typeof s1.scriptSaved === "number" || typeof s1.scriptSaved === "string") {
          this.scriptTypeLocked = Number(s1.scriptSaved) === 1;
        }
        // 兼容历史数据：若步骤二/三已有产物，说明步骤一已生成，应自动锁定第一步编辑
        if (!this.step1Generated && (Array.isArray(row.roles) && row.roles.length || Array.isArray(row.storyboards) && row.storyboards.length)) {
          this.step1Generated = true;
        }
        if (!this.scriptTypeLocked && this.step1Generated) {
          this.scriptTypeLocked = true;
        }
        if (Array.isArray(row.roles)) {
          this.roles = row.roles;
        } else if (m.step2 && Array.isArray(m.step2.roles)) {
          this.roles = m.step2.roles;
        }
        if (Array.isArray(row.scenes)) {
          this.sceneItems = row.scenes;
        } else if (m.step2 && Array.isArray(m.step2.scenes)) {
          this.sceneItems = m.step2.scenes;
        }
        if (Array.isArray(row.materials) || (m.step2 && Array.isArray(m.step2.materials))) {
          this.applyStep2MaterialsSplitFromRow(row, m);
        } else {
          this.materialItems = [];
          this.activeMaterialId = null;
        }
        if (Array.isArray(row.storyboards)) {
          this.storyboards = this.mergeStoryboardsWithEpisodesShots(row.storyboards, row.episodes);
        } else if (m.step3 && Array.isArray(m.step3.storyboards)) {
          this.storyboards = this.mergeStoryboardsWithEpisodesShots(m.step3.storyboards, row.episodes);
        }
        if (row.models && typeof row.models === "object") {
          if (row.models.textModelCode) this.textModelCode = row.models.textModelCode;
          if (row.models.imageModelCode) this.imageModelCode = row.models.imageModelCode;
          if (row.models.videoModelCode) this.videoModelCode = row.models.videoModelCode;
        } else if (m.models && typeof m.models === "object") {
          if (m.models.textModelCode) this.textModelCode = m.models.textModelCode;
          if (m.models.imageModelCode) this.imageModelCode = m.models.imageModelCode;
          if (m.models.videoModelCode) this.videoModelCode = m.models.videoModelCode;
        }
        if (this.roles[0]) this.activeRoleId = this.roles[0].id;
        else this.activeRoleId = null;
        if (this.sceneItems[0]) this.activeSceneId = this.sceneItems[0].id;
        else this.activeSceneId = null;
        if (localStorage.getItem("user_token")) {
          await this.loadDefaultAiModels();
          this.pruneStep2WatchListByEntityPreview();
          await this.resumeStep2ImageTasksFromServer();
        }
        this.clampActiveStyleForProjectType();
        this.syncSessionCachesFromState();
        this.mergeProjectIntoHomeCache();
      } catch (e) {
        this._hydratedFromServer = false;
      }
    },
    openScriptPreviewModal() {
      this.scriptPreviewModalVisible = true;
    },
    closeScriptPreviewModal() {
      this.scriptPreviewModalVisible = false;
    },
    clearProjectStep23SessionCaches() {
      const id = this.project && this.project.id;
      if (!id) return;
      try {
        sessionStorage.removeItem(this.ssKey("roles"));
        sessionStorage.removeItem(this.ssKey("scenes"));
        sessionStorage.removeItem(this.ssKey("materials"));
        sessionStorage.removeItem(this.ssKey("products"));
        sessionStorage.removeItem(this.ssKey("storyboards"));
        sessionStorage.removeItem(step1TaskIdStorageKey(id));
      } catch (e) {
        /* ignore */
      }
    },
    async handleStep1RegenerateReset() {
      if (this.step1RegenerateDisabled) return;
      const id = this.project && this.project.id;
      if (!id) return;
      if (!localStorage.getItem("user_token")) {
        this.windowAlert("请先登录后再操作");
        return;
      }
      if (this.step1PointsValue <= 0) {
        this.windowAlert(
          "当前文字模型未配置步骤一扣费，请在后端 fa_ai_points_config 中维护 scene_code=step1_generate 的积分规则。"
        );
        return;
      }
      if (!String(this.project.title || "").trim()) {
        this.windowAlert("请先填写项目名称");
        return;
      }
      if (!String(this.scriptContent || "").trim()) {
        this.windowAlert("请先填写剧本内容");
        return;
      }
      if (this.scriptContentOverLimit) {
        this.windowAlert(
          `剧本单次上限 ${this.step1ScriptMaxChars} 字（1 万字 / 10 集），请精简后重试。`
        );
        return;
      }
      const runRegenerate = async () => {
        try {
          const id = this.project && this.project.id;
          if (!id) return;
          this.step1Regenerating = true;
          const res = await userApi.resetProjectForStep1Regenerate({ id });
          if (res && typeof res.scriptContentKept === "string") {
            this.scriptContent = res.scriptContentKept;
          }
          this.step1Generated = false;
          this.scriptTypeLocked = false;
          this.roles = [];
          this.sceneItems = [];
          this.materialItems = [];
          this.storyboards = [];
          this.activeRoleId = null;
          this.activeSceneId = null;
          this.activeMaterialId = null;
          this.setStep1TaskId(0);
          this.clearProjectStep23SessionCaches();
          this.persistStep1State({ scriptSaved: false });
          await this.hydrateProjectFromApi();
          this.goToStep(1);
          this.step1Regenerating = false;
          this.step1Generating = true;
          await this.submitStep1GenerateTask();
        } catch (e) {
          this.step1Generating = false;
          this.windowAlert(e && e.message ? e.message : "重新生成失败");
        } finally {
          this.step1Regenerating = false;
        }
      };
      this.showConfirmModal({
        title: "重新生成剧本",
        message: `本次将清空步骤二/三落库数据并全剧重生成，预计消耗 ${this.step1PointsLabel}。步骤四已生成视频不会被自动删除。是否继续？`,
        confirmText: "重新生成",
        onConfirm: runRegenerate,
      });
    },
    onScriptTypeClick(value) {
      if (this.scriptTypeLocked) {
        this.windowAlert("已生成的剧本设定后不支持切换生成类型，请重新创建项目。");
        return;
      }
      this.activeScriptType = value;
    },
    async submitStep1GenerateTask() {
      if (!localStorage.getItem("user_token")) {
        await new Promise(resolve => setTimeout(resolve, 10000));
        this.step1Generated = true;
        this.scriptTypeLocked = true;
        this.step1Generating = false;
        return;
      }
      const createRes = await userApi.createGenerateProjectStep1Task({
        id: this.project.id,
        scriptContent: this.scriptContent,
        activeScriptType: this.activeScriptType,
        episodeCountMode: this.episodeCountMode,
        customEpisodeCount: this.customEpisodeCount,
        activeStyle: this.activeStyle,
        projectType: this.project.type,
        textModelCode: this.textModelCode,
        imageModelCode: this.imageModelCode,
        videoPromptLanguage: this.projectVideoLang
      });
      this.applyStep1EpisodeLimitsFromApi(createRes);
      if (
        createRes.normalizedEpisodeCountMode === "smart" ||
        createRes.normalizedEpisodeCountMode === "custom"
      ) {
        this.episodeCountMode = createRes.normalizedEpisodeCountMode;
      }
      if (
        typeof createRes.normalizedCustomEpisodeCount === "number" &&
        Number.isFinite(createRes.normalizedCustomEpisodeCount)
      ) {
        this.customEpisodeCount = createRes.normalizedCustomEpisodeCount;
      }
      this.clampCustomEpisodeCount();
      const taskId = Number(createRes.taskId || 0);
      if (!taskId) {
        throw new Error("任务创建失败");
      }
      this.setStep1TaskId(taskId);
      this.startStep1TaskPolling(taskId, Number(createRes.pollIntervalMs || 10000));
      let tip = "已提交生成任务，正在后台处理中。可继续操作，刷新后也会自动续轮询。";
      if (createRes.step1AgentHint) {
        tip = `${createRes.step1AgentHint}\n\n${tip}`;
      }
      this.windowAlert(tip);
    },
    async handleStep1Generate() {
      if (this.step1GenerateDisabled) return;
      if (this.step1PointsValue <= 0) {
        this.windowAlert(
          "当前文字模型未配置步骤一扣费，请在后端 fa_ai_points_config 中维护 scene_code=step1_generate 的积分规则。"
        );
        return;
      }
      if (!String(this.project.title || "").trim()) {
        this.windowAlert("请先填写项目名称");
        return;
      }
      if (!String(this.scriptContent || "").trim()) {
        this.windowAlert("请先填写剧本内容");
        return;
      }
      if (this.scriptContentOverLimit) {
        this.windowAlert(
          `剧本单次上限 ${this.step1ScriptMaxChars} 字（1 万字 / 10 集），请精简后重试。`
        );
        return;
      }
      this.step1Generating = true;
      try {
        await this.submitStep1GenerateTask();
      } catch (e) {
        this.step1Generating = false;
        this.windowAlert(e && e.message ? e.message : "生成失败");
      }
    },
    startStep1TaskPolling(taskId, intervalMs = 10000) {
      if (!taskId) return;
      if (this._step1PollTimer) {
        clearTimeout(this._step1PollTimer);
        this._step1PollTimer = null;
      }
      this.step1Generating = true;
      this.step1Progress = 5;
      this.pollStep1TaskUntilDone(taskId, intervalMs);
    },
    async pollStep1TaskUntilDone(taskId, intervalMs = 10000) {
      const start = Date.now();
      const timeoutMs = 45 * 60 * 1000;
      let waitMs = Math.max(1000, intervalMs);
      // 轮询到任务成功/失败；running 时仅等待，不触发额外并发请求
      for (;;) {
        // 后台 tab 不发起请求；visibilitychange 时由 mounted 的 handler 触发补轮询
        if (typeof document !== 'undefined' && document.hidden) {
          await new Promise(resolve => {
            this._step1PollTimer = setPollingTimeout(resolve, waitMs, { forceWhileHidden: true });
          });
          this._step1PollTimer = null;
          continue;
        }
        let st = null;
        try {
          st = await userApi.getGenerateProjectStep1TaskStatus({ taskId });
        } catch (e) {
          // 轮询请求偶发失败时不终止任务，稍后重试，避免前端直接“卡死/失败”
          await new Promise(resolve => {
            this._step1PollTimer = setPollingTimeout(resolve, waitMs, { forceWhileHidden: true });
          });
          this._step1PollTimer = null;
          waitMs = Math.min(4000, waitMs + 500);
          continue;
        }
        const status = String(st.status || "");
        if (st.message) {
          this.step1ProgressMessage = String(st.message);
        }
        if (st.step1SubStep) {
          this.step1SubStep = Number(st.step1SubStep) || 0;
        }
        if (typeof st.progress === "number" && st.progress > 0) {
          this.step1Progress = Math.max(this.step1Progress || 0, Math.min(95, Math.round(st.progress)));
        } else if (status === "running") {
          const elapsed = Date.now() - start;
          const guess = Math.min(90, 8 + Math.floor((elapsed / (5 * 60 * 1000)) * 80));
          this.step1Progress = Math.max(this.step1Progress || 0, guess);
        }
        if (status === "success") {
          this.setStep1TaskId(0);
          const r = st.result || {};
          if (typeof r.scriptContent === "string") this.scriptContent = r.scriptContent;
          this.step1Generated = r.step1Generated !== undefined ? !!r.step1Generated : true;
          this.scriptTypeLocked = r.scriptSaved !== undefined ? !!r.scriptSaved : true;
          this.step1Generating = false;
          this.step1ProgressMessage = "";
          this.step1Progress = 100;
          this.step1SubStep = 0;
          this.persistStep1State({ scriptSaved: true });
          clearTimeout(this._draftSaveTimer);
          if (localStorage.getItem("user_token")) {
            await this.hydrateProjectFromApi();
          }
          await this.flushProjectDraftToApi();
          this.notifyPointsChanged();
          await this.goToStep(2);
          return;
        }
        if (status === "failed") {
          this.setStep1TaskId(0);
          this.step1Generating = false;
          this.step1ProgressMessage = "";
          this.step1Progress = 0;
          this.step1SubStep = 0;
          const errMsg = st.errorMessage || st.message || "步骤一生成失败";
          this.showConfirmModal({
            title: "步骤一生成失败",
            message: `${errMsg}\n\n点击「重试」将以同一任务再次入队，不重复扣分；点击「重新生成」将重新创建任务并扣分。`,
            confirmText: "重试（不重复扣分）",
            cancelText: "关闭",
            onConfirm: async () => {
              if (!userApi.retryStep1Task) {
                this.windowAlert("当前 user.js 缺少 retryStep1Task 接口");
                return;
              }
              try {
                const r = await userApi.retryStep1Task({ id: this._step1TaskId || (st.taskId || 0) });
                if (r && r.taskId) {
                  this.setStep1TaskId(Number(r.taskId));
                  this.startStep1TaskPolling(Number(r.taskId), 5000);
                }
              } catch (e) {
                this.windowAlert(e && e.message ? e.message : "重试失败");
              }
            },
          });
          return;
        }
        if (Date.now() - start > timeoutMs) {
          this.step1Generating = false;
          this.windowAlert(
            "本页已连续等待超过 45 分钟。后台任务可能仍在执行；可刷新页面继续自动轮询，或稍后在项目里查看是否已生成完成。"
          );
          return;
        }
        await new Promise(resolve => {
          this._step1PollTimer = setPollingTimeout(resolve, waitMs, { forceWhileHidden: true });
        });
        this._step1PollTimer = null;
        waitMs = Math.min(3000, waitMs + 200);
      }
    },
    persistProjectTitle() {
      if (!this.project.title) {
        this.windowAlert("项目名称不能为空");
        const fallbackTitle =
          (this.$route.query.title && String(this.$route.query.title)) || "项目";
        this.project.title = fallbackTitle;
        return;
      }
      this.persistProjectToCache();
    },
    persistProjectToCache() {
      const id = this.project.id;
      if (!id) return;
      let list = [];
      try {
        list = JSON.parse(sessionStorage.getItem(HOME_PROJECTS_CACHE_KEY) || "[]");
      } catch (e) {
        list = [];
      }
      const idx = list.findIndex(p => p.id === id);
      if (idx !== -1) {
        list[idx] = {
          ...list[idx],
          title: this.project.title,
          ratio: this.project.ratio,
          type: this.project.type
        };
      } else {
        list.push({
          ...this.project
        });
      }
      sessionStorage.setItem(HOME_PROJECTS_CACHE_KEY, JSON.stringify(list));
      this.$router
        .replace({
          query: {
            ...this.$route.query,
            title: this.project.title || undefined
          }
        })
        .catch(err => {
          if (err && err.name === "NavigationDuplicated") return;
          throw err;
        });
    },
    async goToStep(step) {
      if (step < 1 || step > 3) return;
      if (step > this.highestReachableStep) {
        const blocker = step === 2
          ? "请先完成「剧本生成」"
          : "请先完成「场景角色图片」";
        this.windowAlert(`${blocker}后即可跳转。`);
        return;
      }
      if (step > 1 && this.activeStep === 1 && !this.step1Locked) {
        this.windowAlert("请先点击「开始生成」并完成生成后再进入下一步。");
        return;
      }
      if (step === 3 && this.activeStep === 2 && !this.step2Ready) {
        const missing = this.step2MissingPreviewCount;
        this.windowAlert(missing > 0 ? `请先为所有角色/场景/素材生成或上传图片（还差 ${missing} 个）` : "请先生成或上传图片");
        return;
      }
      if (step === 2 && this.activeStep === 1) {
        this.persistStep1State({ scriptSaved: this.scriptTypeLocked });
        clearTimeout(this._draftSaveTimer);
        await this.flushProjectDraftToApi();
      }
      if (step > 1) {
        // 已登录时真源在服务端：须再次 projectDetail，避免仅读空 session 导致步骤二/三空白（刷新才恢复）
        await this.ensureGeneratedData(step);
      }
      const currentStepInQuery = Number(this.$route.query.step || 1);
      this.activeStep = step;
      // 重复点击当前步骤时，不再触发重复导航（VueRouter 会抛 NavigationDuplicated）。
      if (currentStepInQuery === step) return;
      this.$router
        .replace({
          query: {
            ...this.$route.query,
            step: String(step)
          }
        })
        .catch(err => {
          if (err && err.name === "NavigationDuplicated") return;
          throw err;
        });
    },
    /**
     * @param {number} [targetStep] 即将进入的步骤（goToStep 在写入 activeStep 之前传入，用于判断是否要拉 projectDetail）
     */
    async ensureGeneratedData(targetStep) {
      const stepRef = Math.max(1, Math.min(3, Number(targetStep) || this.activeStep || 1));
      const hasToken = !!localStorage.getItem("user_token");
      if (!this.scriptContent) {
        const cachedStoryboards = this.readSessionList("storyboards", LEGACY_SESSION_KEYS.storyboards);
        const cachedRoles = this.readSessionList("roles", LEGACY_SESSION_KEYS.roles);
        const cachedScenes = this.readSessionList("scenes", LEGACY_SESSION_KEYS.scenes);
        const cachedMaterials = this.readSessionList("materials", LEGACY_SESSION_KEYS.materials);
        if (cachedStoryboards.length) {
          this.storyboards = cachedStoryboards;
        } else {
          this.storyboards = [];
        }
        if (cachedRoles.length) {
          this.roles = cachedRoles;
          this.activeRoleId = this.roles[0] ? this.roles[0].id : null;
        } else {
          this.roles = [];
          this.activeRoleId = null;
        }
        if (cachedScenes.length) {
          this.sceneItems = cachedScenes;
          this.activeSceneId = this.sceneItems[0] ? this.sceneItems[0].id : null;
        } else {
          this.sceneItems = [];
          this.activeSceneId = null;
        }
        if (cachedMaterials.length) {
          this.materialItems = cachedMaterials;
          this.activeMaterialId = this.materialItems[0] ? this.materialItems[0].id : null;
        } else {
          this.materialItems = [];
          this.activeMaterialId = null;
        }
        this.mergeLegacyProductSessionIntoMaterials(this.readSessionList("products", null));
        return;
      }
      if (hasToken) {
        const pid = Number(this.$route.query.id || 0);
        if (pid && stepRef >= 2) {
          if (this._hydratedFromServer && this._hydratedProjectId === pid) {
            await this.refreshStep2EntityPreviewsFromServer();
            await this.resumeStep2ImageTasksFromServer();
          } else {
            await this.hydrateProjectFromApi();
          }
          return;
        }
        const cachedRoles = this.readSessionList("roles", LEGACY_SESSION_KEYS.roles);
        const cachedScenes = this.readSessionList("scenes", LEGACY_SESSION_KEYS.scenes);
        const cachedMaterials = this.readSessionList("materials", LEGACY_SESSION_KEYS.materials);
        const cachedStoryboards = this.readSessionList("storyboards", LEGACY_SESSION_KEYS.storyboards);
        if (!this.roles.length && cachedRoles.length) {
          this.roles = cachedRoles;
          this.activeRoleId = this.roles[0] ? this.roles[0].id : null;
        }
        if (!this.sceneItems.length && cachedScenes.length) {
          this.sceneItems = cachedScenes;
          this.activeSceneId = this.sceneItems[0] ? this.sceneItems[0].id : null;
        }
        if (!this.materialItems.length && cachedMaterials.length) {
          this.materialItems = cachedMaterials;
          this.activeMaterialId = this.materialItems[0] ? this.materialItems[0].id : null;
        }
        this.mergeLegacyProductSessionIntoMaterials(this.readSessionList("products", null));
        if (!this.storyboards.length && cachedStoryboards.length) {
          this.storyboards = cachedStoryboards;
        }
        if (pid) {
          const emptyResources =
            (!Array.isArray(this.roles) || !this.roles.length) &&
            (!Array.isArray(this.sceneItems) || !this.sceneItems.length) &&
            (!Array.isArray(this.materialItems) || !this.materialItems.length);
          const emptyStoryboards = !Array.isArray(this.storyboards) || !this.storyboards.length;
          const needServer =
            (stepRef >= 2 || this.step1Generated) && (emptyResources || emptyStoryboards);
          if (needServer) {
            await this.hydrateProjectFromApi();
          }
        }
        return;
      }
      if (!this.roles.length) {
        const cachedRoles = this.readSessionList("roles", LEGACY_SESSION_KEYS.roles);
        this.roles = cachedRoles.length ? cachedRoles : [];
        this.activeRoleId = this.roles[0] ? this.roles[0].id : null;
      }
      if (!this.sceneItems.length) {
        const cachedScenes = this.readSessionList("scenes", LEGACY_SESSION_KEYS.scenes);
        this.sceneItems = cachedScenes.length ? cachedScenes : [];
        this.activeSceneId = this.sceneItems[0] ? this.sceneItems[0].id : null;
      }
      if (!this.materialItems.length) {
        const cachedMaterials = this.readSessionList("materials", LEGACY_SESSION_KEYS.materials);
        this.materialItems = cachedMaterials.length ? cachedMaterials : [];
        this.activeMaterialId = this.materialItems[0] ? this.materialItems[0].id : null;
      }
      this.mergeLegacyProductSessionIntoMaterials(this.readSessionList("products", null));
      if (!this.storyboards.length) {
        const cachedStoryboards = this.readSessionList("storyboards", LEGACY_SESSION_KEYS.storyboards);
        this.storyboards = cachedStoryboards.length ? cachedStoryboards : [];
        if (this.storyboards.length) this.persistStoryboardsCache();
      }
    },
    persistResourceCaches() {
      sessionStorage.setItem(this.ssKey("roles"), JSON.stringify(this.roles));
      sessionStorage.setItem(this.ssKey("scenes"), JSON.stringify(this.sceneItems));
      sessionStorage.setItem(this.ssKey("materials"), JSON.stringify(this.materialItems));
      this.scheduleSaveProjectDraft();
    },
    hasPreviewImage(preview) {
      return !!String(preview || "").trim();
    },
    getResourceThumb(item) {
      if (!item || typeof item !== "object") return "";
      const preview = String(item.preview || "").trim();
      if (preview) return preview;
      return String(item.avatar || "").trim();
    },
    isPlaceholderAsset(src) {
      const s = String(src || "").trim();
      if (!s) return true;
      return /dummyimage\.com/i.test(s);
    },
    isResourceGenerated(item) {
      if (!item) return false;
      const thumb = this.getResourceThumb ? this.getResourceThumb(item) : (item.avatar || item.cover || item.preview);
      const hasThumb = String(thumb || "").trim() && !this.isPlaceholderAsset(thumb);
      const preview = String(item.preview || "").trim();
      const hasPreview = preview && !this.isPlaceholderAsset(preview);
      return Boolean(hasThumb || hasPreview);
    },
    randomPreviewUrl() {
      return (
        "https://dummyimage.com/420x620/" +
        Math.floor(Math.random() * 900 + 100) +
        "/111.png&text=" + Math.floor(Math.random() * 9 + 1)
      );
    },
    step2EntityIdSetForTab(tab) {
      const list =
        tab === "role" ? this.roles : tab === "scene" ? this.sceneItems : this.materialItems;
      if (!Array.isArray(list)) return new Set();
      return new Set(
        list.map(it => Number(it && it.id)).filter(id => Number.isFinite(id) && id > 0)
      );
    },
    isTabBatchBusy(tab) {
      if (tab !== "role" && tab !== "scene" && tab !== "material") return false;
      if (this.batchGeneratingByTab[tab]) return true;
      const ids = this.step2EntityIdSetForTab(tab);
      return (this.step2WatchList || []).some(j => ids.has(Number(j.entityId)));
    },
    setResourceSubTab(tab) {
      if (tab === "role" || tab === "scene" || tab === "material") {
        this.resourceSubTab = tab;
        void this.refreshStep2EntityPreviewsFromServer();
      }
    },
    mergeEntityPreviewsFromProjectRow(row) {
      if (!row || typeof row !== "object") return;
      const patchList = (list, incoming) => {
        if (!Array.isArray(list) || !Array.isArray(incoming)) return;
        const byId = new Map(incoming.map(it => [Number(it && it.id), it]));
        const localIds = new Set(list.map(it => Number(it && it.id)));
        // Insert new items from server that don't exist locally
        for (const src of incoming) {
          const id = Number(src && src.id);
          if (!id) continue;
          if (!localIds.has(id)) {
            list.push({
              id,
              type: src.type || src.resourceType || "",
              name: src.name || "",
              age: src.age || "",
              gender: src.gender || "",
              summary: src.summary || "",
              description: src.description || "",
              avatar: src.avatar || "",
              preview: src.preview || ""
            });
          }
        }
        // Update preview/avatar for existing local items
        for (const it of list) {
          if (!it || !it.id) continue;
          const src = byId.get(Number(it.id));
          if (!src) continue;
          const pv = String(src.preview || "").trim();
          const av = String(src.avatar || "").trim();
          if (pv) it.preview = pv;
          if (av) it.avatar = av;
        }
      };
      patchList(this.roles, row.roles);
      patchList(this.sceneItems, row.scenes);
      patchList(this.materialItems, row.materials);
    },
    async refreshStep2EntityPreviewsFromServer() {
      const pid = Number((this.project && this.project.id) || 0);
      if (!pid || !localStorage.getItem("user_token")) return;
      try {
        // 替换重型 projectDetail → 轻量 getProjectEntities（只返 entity preview，10-50x 更快）
        const row = await userApi.getProjectEntities({ projectId: pid });
        if (Number((this.project && this.project.id) || 0) !== pid) return;
        if (!row || typeof row !== "object") return;
        // row = { projectId, roles, scenes, materials }，与 projectDetail 中 meta.step2.* 结构一致
        const roles = Array.isArray(row.roles) ? row.roles : [];
        const scenes = Array.isArray(row.scenes) ? row.scenes : [];
        const materials = Array.isArray(row.materials) ? row.materials : [];
        // 合并模式：用服务端返回的 preview 增量更新本地数组，保留用户未提交的 description/summary 等本地编辑
        this.mergeEntityPreviewsFromProjectRow({ roles, scenes, materials, id: pid });
        this.pruneStep2WatchListByEntityPreview();
        this.persistResourceCaches();
      } catch (e) {
        /* ignore */
      }
    },
    pruneStep2WatchListByEntityPreview() {
      const list = Array.isArray(this.step2WatchList) ? this.step2WatchList : [];
      const next = list.filter(job => {
        const it = this.findEntityItemById(job.entityId);
        if (!it) return true;
        return !this.hasPreviewImage(this.getResourceThumb(it));
      });
      if (next.length !== list.length) {
        this.step2WatchList = next;
        if (!next.length) {
          this.clearStep2PollTimer();
        }
      }
    },
    selectResourceItem(item) {
      if (!item) return;
      if (this.resourceSubTab === "role") this.activeRoleId = item.id;
      else if (this.resourceSubTab === "scene") this.activeSceneId = item.id;
      else this.activeMaterialId = item.id;
    },
    isResourceItemActive(item) {
      if (!item) return false;
      if (this.resourceSubTab === "role") return this.activeRoleId === item.id;
      if (this.resourceSubTab === "scene") return this.activeSceneId === item.id;
      return this.activeMaterialId === item.id;
    },
    itemPreviewLabel(item) {
      const id = Number(item && item.id);
      if (id && (this.step2WatchList || []).some(j => Number(j.entityId) === id && j.busy)) {
        return "生成中…";
      }
      return this.hasPreviewImage(this.getResourceThumb(item)) ? "已完成" : "待生成";
    },
    async saveCurrentResource() {
      if (!this.currentResourceEntity) return;
      this.persistResourceCaches();
      try {
        await this.persistCurrentEntityToApi();
        const labels = { role: "角色", scene: "场景", material: "素材" };
        this.windowAlert(`${labels[this.resourceSubTab] || "资源"}信息已保存`);
      } catch (e) {
        this.windowAlert(e && e.message ? e.message : "保存失败");
      }
    },
    triggerEntityImageUpload() {
      if (!this.currentResourceEntity) return;
      const el = this.$refs.entityUploadInput;
      if (!el) return;
      try {
        el.value = "";
      } catch (e) {
        /* ignore */
      }
      el.click();
    },
    async onEntityImageSelected(e) {
      const entity = this.currentResourceEntity;
      if (!entity) return;
      const file = e && e.target && e.target.files ? e.target.files[0] : null;
      if (!file) return;
      if (!localStorage.getItem("user_token")) {
        this.windowAlert("请先登录再上传图片");
        return;
      }
      try {
        const r = await userApi.uploadAsset({
          file,
          assetType: this.resourceSubTab,
          name: entity && entity.name ? entity.name : "entity"
        });
        const url = r && (r.cover || r.url) ? String(r.cover || r.url) : "";
        if (!url.trim()) {
          this.windowAlert("上传失败：未返回图片地址");
          return;
        }
        entity.preview = url;
        if (!String(entity.avatar || "").trim() || this.isPlaceholderAsset(entity.avatar)) {
          entity.avatar = url;
        }
        this.persistResourceCaches();
        await this.persistCurrentEntityToApi();
        this.windowAlert("图片已上传并替换当前项");
      } catch (err) {
        this.windowAlert(err && err.message ? err.message : "上传失败");
      }
    },
    async persistCurrentEntityToApi() {
      const entity = this.currentResourceEntity;
      if (!entity) return;
      if (!localStorage.getItem("user_token") || !entity.id) return;
      clearTimeout(this._draftSaveTimer);
      const payload = {
        id: entity.id,
        name: entity.name || "",
        age: entity.age || "",
        gender: entity.gender || "",
        summary: entity.summary || "",
        avatar: entity.avatar || "",
        preview: entity.preview || ""
      };
      // description/summary 字段：禁止用空字符串覆盖后端已有值（允许用户清空，但必须显式操作）
      // 显式判断字段是否在表单中有值（不为 null/undefined）才发送，避免 textarea 空内容静默清库
      if (entity.description !== undefined && entity.description !== null) {
        payload.description = entity.description || "";
      }
      await userApi.saveProjectEntity(payload);
    },
    downloadCurrentEntityImage() {
      const entity = this.currentResourceEntity;
      if (!entity) return;
      const u = String(entity.preview || "").trim();
      if (!u) return;
      window.open(u, "_blank");
    },
    /** 步骤二：实体图异步任务轮询（依赖 ai:step1-worker 或 taskListener 消费队列） */
    async pollEntityImageTaskUntilDone(taskId, intervalMs = 2000, maxWaitMs = 900000) {
      const started = Date.now();
      while (Date.now() - started < maxWaitMs) {
        const st = await userApi.getGenerateEntityImageTaskStatus({ taskId });
        const status = String(st.status || "");
        if (status === "success") {
          return st;
        }
        if (status === "failed") {
          throw new Error(st.errorMessage || st.message || "图片生成失败");
        }
        await new Promise(r => setTimeout(r, intervalMs));
      }
      throw new Error(
        "等待图片生成超时。请确认服务器正在运行：php think ai:step1-worker，或已配置定时访问 UserApi/taskListener"
      );
    },
    clearStep2PollTimer() {
      if (this._step2PollTimer) {
        clearTimeout(this._step2PollTimer);
        clearInterval(this._step2PollTimer);
        this._step2PollTimer = null;
      }
      this._step2PollInFlight = false;
    },
    /** 将步骤二异步逻辑串成单队列，避免 resume 的轮询与批量/单条 await 循环并发写同一列表 */
    runSerialStep2(fn) {
      const run = (this._step2Serial || Promise.resolve()).then(() => fn());
      this._step2Serial = run.catch(() => {});
      return run;
    },
    /** 切换项目或重新拉详情时清空步骤二队列 UI（服务端任务仍在库中，由 resume 再拉） */
    clearStep2BackgroundPoll() {
      this.clearStep2PollTimer();
      this.step2WatchList = [];
    },
    startStep2BackgroundPoll(intervalMs = 2000) {
      this.pruneStep2WatchListByEntityPreview();
      if (!Array.isArray(this.step2WatchList) || !this.step2WatchList.length) {
        this.clearStep2PollTimer();
        return;
      }
      if (this._step2PollTimer || this._step2PollInFlight) return;
      // 基础间隔按任务数自适应：单任务 2s，10 个任务 4s，避免大批量时仍 2s/轮
      const base = Math.max(2000, intervalMs > 0 ? intervalMs : 2000);
      const sz = Math.max(1, this.step2WatchList.length);
      const scaled = Math.min(6000, base + Math.floor((sz - 1) * 250));
      const scheduleNext = () => {
        if (!this.step2WatchList.length) {
          this.clearStep2PollTimer();
          return;
        }
        // 抖动 ±20%，避免多 tab / 多用户同步打点
        const jitter = Math.floor(scaled * (0.2 * (Math.random() * 2 - 1)));
        const wait = Math.max(2000, scaled + jitter);
        this._step2PollTimer = setTimeout(() => {
          this._step2PollTimer = null;
          void this.tickStep2WatchOnce().then(() => {
            if (this.step2WatchList.length) {
              scheduleNext();
            } else {
              this.clearStep2PollTimer();
            }
          });
        }, wait);
      };
      void this.tickStep2WatchOnce().then(() => {
        if (this.step2WatchList.length) {
          scheduleNext();
        }
      });
    },
    mergeStep2WatchJobs(newJobs) {
      if (!Array.isArray(newJobs) || !newJobs.length) return;
      const m = new Map(this.step2WatchList.map(x => [Number(x.taskId), x]));
      for (const j of newJobs) {
        const tid = Number(j.taskId);
        if (!tid) continue;
        m.set(tid, {
          taskId: tid,
          entityId: Number(j.entityId) || 0,
          entityName: (j.entityName && String(j.entityName)) || "",
          busy: true
        });
      }
      this.step2WatchList = Array.from(m.values());
    },
    findEntityItemById(entityId) {
      const id = Number(entityId);
      if (!id) return null;
      const all = [...this.roles, ...this.sceneItems, ...this.materialItems];
      for (const it of all) {
        if (it && Number(it.id) === id) return it;
      }
      return null;
    },
    applyPreviewToEntityId(entityId, preview) {
      const p = String(preview || "").trim();
      if (!p) return;
      const it = this.findEntityItemById(entityId);
      if (!it) return;
      it.preview = p;
      if (!String(it.avatar || "").trim() || this.isPlaceholderAsset(it.avatar)) {
        it.avatar = p;
      }
    },
    async tickStep2WatchOnce() {
      if (this._step2PollInFlight) return false;
      // 切到后台 tab 时直接跳过整轮：让浏览器空闲时不再压服务器
      if (typeof document !== 'undefined' && document.hidden) return false;
      this._step2PollInFlight = true;
      try {
      const jobs = Array.isArray(this.step2WatchList) ? [...this.step2WatchList] : [];
      if (!jobs.length) return true;
      const next = [];
      const hadLen = jobs.length;
      // 一次请求拿所有 taskId 状态，避免 N 个串行请求把 DB / 鉴权 / 连接池打爆
      const taskIds = jobs.map(j => Number(j.taskId)).filter(n => n > 0);
      const statusByTaskId = {};
      if (taskIds.length === 1) {
        // 单任务保持原有单点接口，避免无谓改动
        try {
          const st = await userApi.getGenerateEntityImageTaskStatus({ taskId: taskIds[0] });
          statusByTaskId[taskIds[0]] = st;
        } catch (e) {
          // 网络错误时把任务留在 next，等待下一轮重试
          next.push(...jobs);
          this.step2WatchList = next;
          return false;
        }
      } else if (taskIds.length > 1) {
        try {
          const batch = await userApi.batchGetGenerateEntityImageTaskStatus({ taskIds });
          const list = (batch && Array.isArray(batch.tasks)) ? batch.tasks : [];
          list.forEach(t => {
            const tid = Number(t.taskId || t.id || 0);
            if (tid > 0) statusByTaskId[tid] = t;
          });
        } catch (e) {
          // 批接口失败时降级：按单点逐个查（避免完全停摆）
          for (const tid of taskIds) {
            try {
              const st = await userApi.getGenerateEntityImageTaskStatus({ taskId: tid });
              statusByTaskId[tid] = st;
            } catch (e2) {
              /* 单点也失败的任务留到下一轮 */
            }
          }
        }
      }
      for (const job of jobs) {
        const tid = Number(job.taskId);
        if (!tid) continue;
        const localItem = this.findEntityItemById(job.entityId);
        if (localItem && this.hasPreviewImage(this.getResourceThumb(localItem))) {
          continue;
        }
        const st = statusByTaskId[tid];
        if (!st) {
          next.push(job);
          continue;
        }
        const status = String(st.status || "");
        if (status === "success" || status === "not_found") {
          const preview = String(
            (st && st.preview) || (st && st.result && st.result.preview) || ""
          ).trim();
          if (preview) {
            this.applyPreviewToEntityId(job.entityId, preview);
          }
          continue;
        }
        if (status === "failed") {
          const errMsg = String(
            (st && st.errorMessage) || (st && st.message) || "图片生成失败"
          ).trim();
          if (errMsg) {
            console.warn("[step2] task failed", tid, errMsg);
          }
          continue;
        }
        next.push(job);
      }
      this.step2WatchList = next;
      if (hadLen && !next.length) {
        this.clearStep2PollTimer();
        this.persistResourceCaches();
        this.notifyPointsChanged();
      }
      return next.length === 0;
      } finally {
        this._step2PollInFlight = false;
      }
    },
    async resumeStep2ImageTasksFromServer() {
      const pid = Number((this.project && this.project.id) || 0);
      if (!pid || !localStorage.getItem("user_token")) return;
      void this.runSerialStep2(async () => {
        let data;
        try {
          data = await userApi.listStep2EntityImageTasksForProject({ projectId: pid });
        } catch (e) {
          return;
        }
        if (Number((this.project && this.project.id) || 0) !== pid) return;
        const tasks = (data && data.tasks) || [];
        if (!tasks.length) return;
        this.mergeStep2WatchJobs(
          tasks
            .map(t => ({
              taskId: Number(t.taskId) || 0,
              entityId: Number(t.entityId) || 0,
              entityName: (t.entityName && String(t.entityName)) || ""
            }))
            .filter(j => j.taskId > 0 && j.entityId > 0)
        );
        this.pruneStep2WatchListByEntityPreview();
        if (!this.step2WatchList.length) return;
        this.startStep2BackgroundPoll(2000);
      });
    },
    async runStep2WatchLoopUntilEmpty(intervalMs = 2000, maxWaitMs = 900000) {
      const t0 = Date.now();
      while (this.step2WatchList.length && Date.now() - t0 < maxWaitMs) {
        await this.tickStep2WatchOnce();
        if (this.step2WatchList.length) {
          await new Promise(r => setTimeout(r, intervalMs));
        }
      }
      if (this.step2WatchList.length) {
        throw new Error(
          "等待图片生成超时。请确认服务器正在运行：php think ai:step1-worker，或已配置定时访问 UserApi/taskListener"
        );
      }
      this.persistResourceCaches();
      this.notifyPointsChanged();
    },
    async handleBatchGenerate() {
      const typeLabels = { role: "角色", scene: "场景", material: "素材" };
      const tab = this.resourceSubTab;
      if (this.resourceGenerating) return;
      if (this.isTabBatchBusy(tab)) return;
      this.$set(this.batchGeneratingByTab, tab, true);
      try {
      const label = typeLabels[tab];

      await this.ensureGeneratedData();

      const list =
        tab === "role" ? this.roles : tab === "scene" ? this.sceneItems : this.materialItems;
      if (!list.length) {
        this.windowAlert(`暂无${label}数据。请先在步骤 1 填写剧本以生成示例列表，或通过缓存恢复项目。`);
        return;
      }

      // 已登录：先为每条实体创建 fa_ai_project_task（全部落库），再统一轮询；刷新页面可由 resume 接续
      if (localStorage.getItem("user_token")) {
        if (this.step2SinglePointsValue <= 0) {
          this.windowAlert(
            "当前图片模型未配置步骤二扣费，请在后端 fa_ai_points_config 中维护 scene_code=step2_generate_image 的积分规则。"
          );
          return;
        }
        // 仅补全未生成的空图：已有图（含手动上传）不重复生成
        const targets = list.filter(
          it => it && it.id && !this.hasPreviewImage(this.getResourceThumb(it))
        );
        if (!targets.length) {
          this.windowAlert(`当前${label}均已有图片，无需批量生成`);
          return;
        }
        await this.runSerialStep2(async () => {
          const pid = Number((this.project && this.project.id) || 0);
          if (!pid) return;
          // 替换原 for-await 调 N 次 generateEntityImage：单次 batchGenerateEntityImages 提交，
          // 后端一次性预检积分、入队、取消同 entityId 旧 pending；前端少 N-1 次 HTTP。
          const entityIds = targets.map(it => Number(it.id)).filter(n => n > 0);
          let created;
          try {
            created = await userApi.batchGenerateEntityImages({ entityIds });
          } catch (e) {
            this.windowAlert("批量提交图片任务失败：" + (e && e.message ? e.message : e));
            return;
          }
          const jobs = [];
          let pollMs = 2000;
          if (created && Array.isArray(created.jobs)) {
            created.jobs.forEach(j => {
              if (j && j.taskId) {
                jobs.push({
                  taskId: Number(j.taskId),
                  entityId: Number(j.entityId),
                  entityName: (() => {
                    const it = targets.find(x => Number(x.id) === Number(j.entityId));
                    return (it && it.name && String(it.name)) || "";
                  })()
                });
              }
            });
            if (created.pollIntervalMs && created.pollIntervalMs > 0) {
              pollMs = Number(created.pollIntervalMs);
            }
          }
          if (!jobs.length) {
            const errs = (created && Array.isArray(created.errors)) ? created.errors : [];
            this.windowAlert(errs.length ? errs.map(x => `#${x.entityId}: ${x.msg}`).join("；") : "创建图片任务失败，请检查网络、积分与模型配置");
            return;
          }
          if (Number((this.project && this.project.id) || 0) !== pid) return;
          this.mergeStep2WatchJobs(jobs);
          this.startStep2BackgroundPoll(pollMs);
          this.persistResourceCaches();
          this.windowAlert(
            `已提交 ${jobs.length} 个${label}图片任务，正在生成中。`
          );
        });
        return;
      }

      const missing = list.filter(i => !this.hasPreviewImage(i.preview));
      if (missing.length === 0) {
        if (!window.confirm(`当前${label}预览图均已生成，是否全部重新生成？`)) return;
        list.forEach(i => {
          i.preview = this.randomPreviewUrl();
        });
        this.persistResourceCaches();
        this.windowAlert(`已重新生成全部${label}预览图`);
        return;
      }

      if (missing.length < list.length) {
        missing.forEach(i => {
          i.preview = this.randomPreviewUrl();
        });
        this.persistResourceCaches();
        this.windowAlert(`已为 ${missing.length} 条${label}补全预览图`);
        return;
      }

      list.forEach(i => {
        i.preview = this.randomPreviewUrl();
      });
      this.persistResourceCaches();
      this.windowAlert(`已生成全部${label}预览图`);
      } finally {
        this.$set(this.batchGeneratingByTab, tab, false);
      }
    },
    openRoleModal(role) {
      this.roleModal.visible = true;
      this.roleModal.mode = role ? "edit" : "create";
      this.roleModal.targetId = role ? role.id : null;
      this.roleForm = role
        ? {
            type: role.type || role.resourceType || "role",
            name: role.name || "",
            age: role.age || "",
            gender: role.gender || "",
            summary: role.summary || "",
            description: role.description || "",
          }
        : {
            type: this.resourceSubTab || "role",
            name: "",
            age: "",
            gender: "",
            summary: "",
            description: "",
          };
    },
    closeRoleModal() {
      this.roleModal.visible = false;
    },
    submitRoleModal() {
      if (!this.roleForm.name) {
        const label = { role: "角色名", scene: "场景名", material: "素材名" }[this.roleForm.type] || "名称";
        this.windowAlert("请输入" + label);
        return;
      }
      const type = this.roleForm.type || "role";
      const label = { role: "角色", scene: "场景", material: "素材" }[type] || "资源";
      const newItem = {
        id: Date.now(),
        type,
        name: this.roleForm.name,
        age: this.roleForm.age || "",
        gender: this.roleForm.gender || "",
        summary: this.roleForm.summary || "",
        description: this.roleForm.description || "",
        avatar: "",
        preview: ""
      };
      if (this.roleModal.mode === "create") {
        if (type === "role") {
          this.roles.unshift(newItem);
          this.activeRoleId = newItem.id;
        } else if (type === "scene") {
          this.sceneItems.unshift(newItem);
          this.activeSceneId = newItem.id;
        } else if (type === "material") {
          this.materialItems.unshift(newItem);
          this.activeMaterialId = newItem.id;
        }
      } else {
        if (type === "role") {
          this.roles = this.roles.map(item =>
            item.id === this.roleModal.targetId ? { ...item, ...newItem } : item
          );
        } else if (type === "scene") {
          this.sceneItems = this.sceneItems.map(item =>
            item.id === this.roleModal.targetId ? { ...item, ...newItem } : item
          );
        } else if (type === "material") {
          this.materialItems = this.materialItems.map(item =>
            item.id === this.roleModal.targetId ? { ...item, ...newItem } : item
          );
        }
      }
      this.persistResourceCaches();
      this.closeRoleModal();
      this.refreshStep2EntityPreviewsFromServer();
    },
    async regenerateCurrentResource() {
      if (this.resourceGenerating || this.isCurrentTabBatchBusy) return;
      this.resourceGenerating = true;
      try {
      const entity = this.currentResourceEntity;
      if (!entity) return;
      // 进入轮询前先清掉「当前选中实体」残留的旧 busy 任务，避免前一次 regen 失败/中断
      // 留下的脏数据卡住新任务。
      const eid0 = Number(entity.id) || 0;
      if (eid0 > 0 && this.step2WatchList && this.step2WatchList.length) {
        this.step2WatchList = this.step2WatchList.filter(
          j => Number(j.entityId) !== eid0
        );
        if (!this.step2WatchList.length) {
          this.clearStep2PollTimer();
        }
      }
      const hadPreview = this.entityHasPreviewImage;
      if (localStorage.getItem("user_token") && entity.id) {
        if (this.step2SinglePointsValue <= 0) {
          this.windowAlert(
            "当前图片模型未配置步骤二扣费，请在后端 fa_ai_points_config 中维护 scene_code=step2_generate_image 的积分规则。"
          );
          return;
        }
        try {
          // 重新生成前先把当前表单内容落库，避免后端读取旧 description
          this.persistResourceCaches();
          await this.persistCurrentEntityToApi();
          const created = await userApi.generateEntityImage({ entityId: entity.id });
          const taskId = created && created.taskId;
          if (!taskId) {
            this.windowAlert("创建图片任务失败");
            return;
          }
          const interval =
            created.pollIntervalMs && created.pollIntervalMs > 0 ? created.pollIntervalMs : 2000;
          const eid = Number(entity.id) || 0;
          await this.runSerialStep2(async () => {
            const pid = Number((this.project && this.project.id) || 0);
            if (!pid) return;
            this.mergeStep2WatchJobs([
              {
                taskId,
                entityId: eid,
                entityName: (entity.name && String(entity.name)) || ""
              }
            ]);
            const st = await this.pollEntityImageTaskUntilDone(taskId, interval);
            const stillHere = Number((this.project && this.project.id) || 0) === pid;
            const preview = String(
              (st && st.preview) || (st && st.result && st.result.preview) || ""
            ).trim();
            if (stillHere && preview) {
              entity.preview = preview;
              // 重新生成后总是把最新预览图同步到 avatar：避免列表缩略图长期指向旧图
              entity.avatar = preview;
              this.persistResourceCaches();
              this.notifyPointsChanged();
              // 把最新 avatar 一并落库，下次重新生成前 persistCurrentEntityToApi
              // 不会用旧的 avatar 把后端 avatar_url 覆盖回去
              try {
                await this.persistCurrentEntityToApi();
              } catch (e) {
                /* ignore: avatar 同步失败不影响本轮结果 */
              }
            }
            this.step2WatchList = (this.step2WatchList || []).filter(
              j => Number(j.taskId) !== Number(taskId)
            );
            if (!this.step2WatchList.length) {
              this.clearStep2PollTimer();
            }
          });
        } catch (e) {
          this.windowAlert(e && e.message ? e.message : "生成失败");
          return;
        }
      } else {
        entity.preview = this.randomPreviewUrl();
        this.persistResourceCaches();
      }
      void hadPreview;
      } finally {
        this.resourceGenerating = false;
      }
    },
    regenerateRole() {
      this.regenerateCurrentResource();
    },
    persistStoryboardsCache() {
      sessionStorage.setItem(this.ssKey("storyboards"), JSON.stringify(this.storyboards));
      this.scheduleSaveProjectDraft();
    },
    goToStoryboardEditor() {
      if (!this.storyboards.length) {
        this.windowAlert("请先生成分集列表");
        return;
      }
      const first = this.storyboards[0];
      this.persistStoryboardsCache();
      this.$router.push({
        path: "/project/storyboard-editor",
        query: {
          id: String(first.id),
          projectId: String(this.project.id)
        }
      });
    },
    openEpisodeModal() {
      this.episodeModal.visible = true;
      this.episodeModal.title = "";
      this.episodeModal.content = "";
    },
    closeEpisodeModal() {
      this.episodeModal.visible = false;
    },
    openEntityPreview(entity) {
      if (!entity) return;
      const preview = String(entity.preview || "").trim();
      if (!preview) return;
      if (this.isPlaceholderAsset(preview)) return;

      this.entityPreviewDialog.visible = true;
      this.entityPreviewDialog.src = preview;
      this.entityPreviewDialog.alt = String(entity.name || "预览");
      this.entityPreviewDialog.entity = entity;
      this.entityPreviewDialog.field = "preview";
    },
    closeEntityPreviewDialog() {
      this.entityPreviewDialog.visible = false;
      this.entityPreviewDialog.src = "";
      this.entityPreviewDialog.alt = "";
      this.entityPreviewDialog.entity = null;
    },
    onEntityPreviewImgError() {
      const e = this.entityPreviewDialog.entity;
      if (e) this.onImgError(e, this.entityPreviewDialog.field || "preview");
    },
    closeStoryboardCoverPreview() {
      this.storyboardCoverPreview = {
        visible: false,
        src: "",
        alt: "",
        item: null
      };
    },
    onStoryboardCoverPreviewImgError() {
      const item = this.storyboardCoverPreview.item;
      if (item) this.onImgError(item, "cover");
    },
    submitEpisodeModal() {
      const content = (this.episodeModal.content || "").trim();
      if (!content) {
        this.windowAlert("请输入本分集内容");
        return;
      }
      if (content.length > 30000) {
        this.windowAlert("本分集内容不超过3万字");
        return;
      }
      const startLen = this.storyboards.length;
      const episodeNum = startLen + 1;
      const title = (this.episodeModal.title || "").trim() || `第 ${episodeNum} 集：新分集`;
      this.storyboards.push({
        id: Date.now(),
        title,
        roles: String(2 + ((episodeNum - 1) % 5)),
        scenes: "1",
        duration: "待定",
        cover: "",
        episodeScript: content
      });
      this.persistStoryboardsCache();
      this.closeEpisodeModal();
      this.windowAlert("已新增 1 条分集");
    },
    async handleRegenerateStoryboardScript() {
      if (!this.scriptContent) {
        this.windowAlert("请先在步骤 1 填写剧本内容后再生成分镜脚本。");
        return;
      }
      if (this.storyboards.length) {
        if (!window.confirm("重新生成将替换当前所有分集及分集下的所有视频记录，是否继续？")) return;
      } else if (!window.confirm("将根据剧本生成分集列表（当前为服务端占位解析，后续接真实语言模型），是否继续？")) {
        return;
      }
      try {
        if (localStorage.getItem("user_token")) {
          const r = await userApi.listProjectStoryboards({ id: this.project.id });
          this.storyboards = Array.isArray(r.storyboards) ? r.storyboards : [];
        } else {
          this.windowAlert("请先登录后再生成分镜脚本");
          return;
        }
        this.persistStoryboardsCache();
        clearTimeout(this._draftSaveTimer);
        await this.flushProjectDraftToApi();
        this.windowAlert("分镜脚本列表已更新");
      } catch (e) {
        this.windowAlert(e && e.message ? e.message : "生成失败");
      }
    },
    showConfirmModal({ title, message, confirmText = "确定", cancelText = "取消", onConfirm }) {
      this.confirmModal = { visible: true, title, message, confirmText, cancelText, onConfirm: typeof onConfirm === "function" ? onConfirm : null };
    },
    closeConfirmModal() {
      this.confirmModal = { ...this.confirmModal, visible: false };
    },
    confirmModalOk() {
      const cb = this.confirmModal.onConfirm;
      this.closeConfirmModal();
      if (typeof cb === "function") {
        try { cb(); } catch (e) { /* ignore */ }
      }
    },
    async handleBatchGenerateStoryboardVideos() {
      if (this.batchSending) return;
      if (!localStorage.getItem("user_token")) {
        this.windowAlert("请先登录后再批量提交");
        return;
      }
      if (!this.storyboards.length) {
        this.windowAlert("暂无分集可提交");
        return;
      }
      if (typeof userApi.batchCreateStoryboardVideoTasks !== "function") {
        this.windowAlert("当前 user.js 缺少批量提交方法，请升级前端");
        return;
      }
      const start = async () => {
        this.batchSending = true;
        try {
          const r = await userApi.batchCreateStoryboardVideoTasks({ id: Number(this.project.id) });
          const queued = Number((r && r.queued) || 0);
          const skipped = Number((r && r.skipped) || 0);
          this.windowAlert(`批量提交完成：成功入队 ${queued} 条；跳过 ${skipped} 条（已排队/无 prompt）。请到「分镜编辑」跟踪进度。`);
        } catch (e) {
          this.windowAlert(e && e.message ? e.message : "批量提交失败");
        } finally {
          this.batchSending = false;
        }
      };
      this.showConfirmModal({
        title: "批量提交全部分镜视频",
        message: `将一次性为「${this.project && this.project.title || ""}」所有分集生成的所有分镜镜头创建视频生成任务；使用项目默认视频模型，按实际积分扣除。确认提交？`,
        confirmText: "提交",
        onConfirm: start,
      });
    },
    confirmDeleteStoryboard(item) {
      if (!item) return;
      if (!window.confirm(`将删除分集「${item.title}」及该分集下的所有视频记录，是否继续？`)) return;
      this.storyboards = this.storyboards.filter(row => row.id !== item.id);
      this.persistStoryboardsCache();
    },
    regenerateEpisode(item) {
      if (!item || !item.id) return;
      if (!localStorage.getItem("user_token")) {
        this.windowAlert("请先登录后再重生本集");
        return;
      }
      const episodeNo = Number(item.episode_no || item.episodeNo || item.no || 0);
      const episodeId = Number(item.id || 0);
      // 防抖：同集已有进行中的任务，不再弹 modal，直接提示
      if (this.isEpisodeRegenerating(item)) {
        this.$message && this.$message.info && this.$message.info(`第 ${episodeNo} 集正在重生中，请稍候`);
        return;
      }
      this.showConfirmModal({
        title: `重生「${item.title}」剧本与分镜`,
        message: "将仅对本集剧本 + 分镜重新生成；将扣除对应步骤一积分，其他集不变。是否继续？",
        confirmText: "重生本集",
        onConfirm: async () => {
          this.regenSubmitting = true;
          try {
            const r = await userApi.regenerateProjectStep1Episode({
              id: Number(this.project.id),
              episodeNo,
              episodeId,
            });
            if (!r) {
              this.windowAlert("请求无响应");
              return;
            }
            const taskId = Number(r.taskId || 0);
            if (taskId > 0) {
              // 注册到队列 → 立刻显示「准备中…」（占位进度 5%），再开始轮询
              const job = {
                taskId,
                episodeId,
                episodeNo,
                episodeTitle: String(item.title || ''),
                status: r.status || 'pending',
                progress: 5,
                message: '任务已提交，准备执行…',
                errorMessage: '',
                shotCount: 0
              };
              this.regenQueue = (this.regenQueue || []).filter(j => j.episodeId !== episodeId);
              this.regenQueue.push(job);
              this.persistRegenQueue();
              if (this.$message && this.$message.success) {
                if (r.dedup) {
                  this.$message.info(`第 ${episodeNo} 集已有重生任务在进行中，已为你继续监听`);
                } else {
                  this.$message.success(`第 ${episodeNo} 集重生任务已提交，正在生成中…`);
                }
              }
              this._startRegenPolling();
            } else {
              // 兼容老的同步响应（理论上不会走到）
              const displayNo = episodeNo || item.no || '?';
              this.windowAlert(`已重生第 ${displayNo} 集，新分镜 ${(r && r.shotCount) || 0} 条。`);
              await this.hydrateProjectFromApi();
              this.persistStoryboardsCache();
            }
          } catch (e) {
            // POST 失败（超时 / 中断 / 后端 504 等）：
            // 1. 服务端可能已经成功入队（task 已创建并扣费），只是响应没回来；
            // 2. 也可能真的失败；
            // 不弹「重生失败」误导用户 → 给一个保守的提示，让其去任务列表/重新点
            const em = e && e.message ? e.message : "请求出错";
            this.windowAlert(`提交请求出错（${em}）。若已扣费，稍候 5~10 秒后再次点击「重生本集」可继续监听任务状态。`);
          } finally {
            this.regenSubmitting = false;
          }
        },
      });
    },
    /** 队列里是否还有未结束的 job */
    isEpisodeRegenerating(item) {
      if (!item) return false;
      if (this.regenSubmitting) return true;
      const id = Number(item.id || 0);
      if (!id) return false;
      const epNo = Number(item.episode_no || item.episodeNo || item.no || 0);
      const queue = this.regenQueue || [];
      return queue.some(j => {
        if (j.status === 'success' || j.status === 'failed' || j.status === 'cancelled') return false;
        if (id && Number(j.episodeId || 0) === id) return true;
        if (epNo && Number(j.episodeNo || 0) === epNo) return true;
        return false;
      });
    },
    /** 取该集当前任务进度（来自队列） */
    episodeRegenProgressFor(item) {
      if (!item) return null;
      const id = Number(item.id || 0);
      const epNo = Number(item.episode_no || item.episodeNo || item.no || 0);
      const queue = this.regenQueue || [];
      const job = queue.find(j => {
        if (id && Number(j.episodeId || 0) === id) return true;
        if (epNo && Number(j.episodeNo || 0) === epNo) return true;
        return false;
      });
      if (!job) return null;
      // 终态后端结果返回后，过 3 秒再清掉队列条目（给用户看结果）
      return job;
    },
    regenStatusLabel(status) {
      const s = String(status || '').toLowerCase();
      if (s === 'pending') return '排队中';
      if (s === 'running') return '执行中';
      if (s === 'success') return '已完成';
      if (s === 'failed') return '失败';
      if (s === 'cancelled') return '已取消';
      return s || '处理中';
    },
    regenStatusClass(status) {
      const s = String(status || '').toLowerCase();
      if (s === 'success') return 'regen-toast-row--done';
      if (s === 'failed') return 'regen-toast-row--fail';
      if (s === 'running') return 'regen-toast-row--running';
      return '';
    },
    _startRegenPolling() {
      if (this._regenPollTimer) return;
      let consecutiveNoChange = 0;
      const baseMs = 2500;
      const capMs = 8000;
      let currentMs = baseMs;
      const poll = async () => {
        this._regenPollTimer = null;
        if (!this.regenQueue || !this.regenQueue.length) {
          return;
        }
        // 后台 tab 直接跳过整轮：让浏览器空闲时不再压服务器；visibilitychange 会触发补一轮
        if (typeof document !== 'undefined' && document.hidden) {
          this._regenPollTimer = setPollingTimeout(poll, currentMs, { forceWhileHidden: false });
          return;
        }
        const active = this.regenQueue.filter(j => j.status !== 'success' && j.status !== 'failed' && j.status !== 'cancelled');
        if (!active.length) {
          // 全部完成，再补一轮成功/失败 toast 后清空
          const okJobs = this.regenQueue.filter(j => j.status === 'success');
          const failJobs = this.regenQueue.filter(j => j.status === 'failed');
          if (okJobs.length && this.$message && this.$message.success) {
            okJobs.forEach(j => {
              this.$message.success(`第 ${j.episodeNo} 集重生成功（${j.shotCount || ''} 条分镜）`);
            });
          }
          if (failJobs.length && this.$message && this.$message.error) {
            failJobs.forEach(j => {
              this.$message.error(`第 ${j.episodeNo} 集重生失败：${j.errorMessage || j.message || ''}`);
            });
          }
          // 重新拉取详情
          try {
            await this.hydrateProjectFromApi();
            this.persistStoryboardsCache();
          } catch (e) { /* ignore */ }
          // 3s 后清空队列（让用户看清 toast）
          setTimeout(() => {
            if (!this.regenQueue.some(j => j.status !== 'success' && j.status !== 'failed' && j.status !== 'cancelled')) {
              this.regenQueue = [];
              this.persistRegenQueue();
            }
          }, 3000);
          this._regenPollTimer = null;
          return;
        }
        // 批量拉任务状态（一次最多 20 个）
        const ids = active.map(j => j.taskId).filter(id => id > 0).slice(0, 20);
        if (!ids.length) {
          this._regenPollTimer = setPollingTimeout(poll, currentMs);
          return;
        }
        let statusChanged = false;
        try {
          const resp = await userApi.getGenerateProjectStep1TaskStatus({ taskIds: ids.join(',') });
          // request() 已经 unwrap res.data，resp 就是 data 对象本身
          if (resp && Array.isArray(resp.tasks)) {
            resp.tasks.forEach(t => {
              const tid = Number(t.id || t.taskId || 0);
              const job = this.regenQueue.find(j => Number(j.taskId || 0) === tid);
              if (job) {
                const prevStatus = job.status;
                const prevProgress = job.progress;
                job.status = String(t.status || job.status);
                job.progress = Math.max(0, Math.min(100, Number(t.progress || 0)));
                job.message = String(t.message || job.message || '');
                if (job.status !== prevStatus || job.progress !== prevProgress) {
                  statusChanged = true;
                }
                if (job.status === 'success' && t.result) {
                  try {
                    const r = typeof t.result === 'string' ? JSON.parse(t.result) : t.result;
                    if (r && r.shotCount) job.shotCount = r.shotCount;
                  } catch (e) { /* ignore */ }
                }
                if (job.status === 'failed') {
                  job.errorMessage = String(t.errorMessage || t.error_message || job.message || '');
                }
              }
            });
            // 强制刷新 UI
            this.regenQueue = [...this.regenQueue];
            this.persistRegenQueue();
          }
        } catch (e) {
          // 静默忽略，下次轮询再试
        }
        // 自适应间隔：状态没变化 → 拉长（最多 8s），变化 → 恢复基础 2.5s
        if (statusChanged) {
          consecutiveNoChange = 0;
          currentMs = baseMs;
        } else {
          consecutiveNoChange += 1;
          currentMs = Math.min(capMs, currentMs + 1000);
        }
        this._regenPollTimer = setPollingTimeout(poll, currentMs);
      };
      this._regenPollTimer = setPollingTimeout(poll, 1000);
    },
    /**
     * 计算 regenQueue 的 sessionStorage key（按用户+项目分桶，便于多项目切换不串）
     */
    _regenStorageKey() {
      try {
        const token = localStorage.getItem("user_token");
        const uid = (() => {
          try {
            const raw = localStorage.getItem("user_info");
            if (!raw) return token ? "anon" : "guest";
            const u = JSON.parse(raw);
            return u && u.id ? String(u.id) : (token ? "anon" : "guest");
          } catch (e) {
            return token ? "anon" : "guest";
          }
        })();
        const pid = this.project && this.project.id ? Number(this.project.id) : 0;
        return `ai_video_regen_queue:${uid}:${pid || 'any'}`;
      } catch (e) {
        return `ai_video_regen_queue:guest`;
      }
    },
    /**
     * 把当前队列（仅活跃任务）写入 sessionStorage，含 taskId 等不变量。
     * 注意：用户名等信息可能在登录切换后过期，hydrate 时会用服务端对账兜底。
     */
    persistRegenQueue() {
      try {
        const queue = Array.isArray(this.regenQueue) ? this.regenQueue : [];
        const active = queue.filter(j => {
          const s = String(j.status || '');
          return s && s !== 'success' && s !== 'failed' && s !== 'cancelled' && s !== 'not_found';
        });
        const key = this._regenStorageKey();
        if (!active.length) {
          sessionStorage.removeItem(key);
          return;
        }
        const snapshot = active.map(j => ({
          taskId: j.taskId,
          episodeId: j.episodeId,
          episodeNo: j.episodeNo,
          episodeTitle: j.episodeTitle || '',
          status: j.status,
          progress: j.progress || 0,
          message: j.message || '',
          savedAt: Date.now()
        }));
        sessionStorage.setItem(key, JSON.stringify({
          projectId: this.project && this.project.id ? Number(this.project.id) : 0,
          jobs: snapshot,
          savedAt: Date.now()
        }));
      } catch (e) {
        // sessionStorage 可能因隐私模式不可用，吞掉
      }
    },
    /**
     * 从 sessionStorage 恢复队列。会和服务端 resumeRegenQueueFromServer 串联：
     *  - 先读 snapshot（保证「哪怕后端列表接口挂了，右下角也能继续显示」）
     *  - 再用服务端的最新 status/progress 覆盖
     */
    restoreRegenQueueFromSession() {
      try {
        const key = this._regenStorageKey();
        const raw = sessionStorage.getItem(key);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!parsed || !Array.isArray(parsed.jobs)) return;
        // 超过 2h 的快照视为过期，避免显示幽灵任务
        const savedAt = Number(parsed.savedAt || 0);
        if (savedAt && Date.now() - savedAt > 2 * 3600 * 1000) {
          sessionStorage.removeItem(key);
          return;
        }
        // 仅恢复仍然 pending/running 的；终态立刻丢弃
        const restored = parsed.jobs.filter(j => {
          const s = String(j.status || '');
          return s === 'pending' || s === 'running';
        });
        if (restored.length) {
          // 合并：已有的（同 taskId）保留前端数据（用户已有进度），添加新的
          const existingById = new Map((this.regenQueue || []).map(j => [Number(j.taskId || 0), j]));
          restored.forEach(j => {
            const tid = Number(j.taskId || 0);
            if (!tid) return;
            if (!existingById.has(tid)) {
              this.regenQueue.push(j);
              existingById.set(tid, j);
            }
          });
        }
      } catch (e) {
        /* ignore */
      }
    },
    /**
     * 登录后从服务端拉取当前用户下所有项目（包括但不限于当前项目）的 regen 任务，对账后重建队列 + 启动轮询。
     * 这一步是「刷新后进度不丢」的关键 source of truth。
     */
    async resumeRegenQueueFromServer() {
      if (typeof userApi.listMyStep1RegenerateEpisodeTasks !== "function") return;
      let resp;
      try {
        const projectId = this.project && this.project.id ? Number(this.project.id) : 0;
        resp = await userApi.listMyStep1RegenerateEpisodeTasks({ projectId });
      } catch (e) {
        // 接口失败：用 sessionStorage 旧数据继续显示，轮询继续尝试
        this._startRegenPolling();
        return;
      }
      if (!resp || !Array.isArray(resp.tasks)) {
        this._startRegenPolling();
        return;
      }
      const tasks = resp.tasks;
      // 没有 pending/running 的任务，但有 5 分钟内刚结束的 → 给最后一次 toast
      const recentlyDone = tasks.filter(t => t.status === 'success' || t.status === 'failed');
      const active = tasks.filter(t => t.status === 'pending' || t.status === 'running');
      // 合并到队列（保留用户已经在看的活跃项）
      const existingByKey = new Map((this.regenQueue || []).map(j => [Number(j.taskId || 0), j]));
      let added = 0;
      tasks.forEach(t => {
        const tid = Number(t.taskId || 0);
        if (!tid) return;
        const epId = Number(t.episodeId || 0);
        const epNo = Number(t.episodeNo || 0);
        // 跳过已结束的（用 recentlyDone 单次 toast 处理）
        if (t.status !== 'pending' && t.status !== 'running') return;
        const existing = existingByKey.get(tid);
        if (existing) {
          // 用服务端最新数据覆盖（防止前端 stale）
          existing.status = t.status;
          existing.progress = Math.max(0, Math.min(100, Number(t.progress || 0)));
          existing.message = t.message || existing.message || '';
        } else {
          this.regenQueue.push({
            taskId: tid,
            episodeId: epId,
            episodeNo: epNo,
            episodeTitle: '',
            status: t.status,
            progress: Math.max(0, Math.min(100, Number(t.progress || 0))),
            message: t.message || '任务已提交，准备执行…',
            errorMessage: ''
          });
          added++;
        }
      });
      // 强制刷新 UI
      this.regenQueue = [...(this.regenQueue || [])];
      // 有刚结束的：补一条 toast（但避免无限重复）
      const lastResumeSig = sessionStorage.getItem(this._regenStorageKey() + ':_resumeSig') || '';
      const sig = recentlyDone.map(t => Number(t.taskId || 0) + ':' + String(t.status || '')).join(',');
      if (sig && sig !== lastResumeSig) {
        recentlyDone.forEach(t => {
          if (t.status === 'success') {
            this.$message && this.$message.success && this.$message.success(`第 ${t.episodeNo || t.episodeId || '?'} 集重生已成功完成（刷新页面后自动恢复）`);
          } else if (t.status === 'failed') {
            this.$message && this.$message.error && this.$message.error(`第 ${t.episodeNo || t.episodeId || '?'} 集重生失败：${t.errorMessage || t.message || ''}`);
          }
        });
        try {
          sessionStorage.setItem(this._regenStorageKey() + ':_resumeSig', sig);
        } catch (e) { /* ignore */ }
        // 拉一次最新详情，让用户立刻看到新数据
        try {
          await this.hydrateProjectFromApi();
          this.persistStoryboardsCache();
        } catch (e) { /* ignore */ }
      }
      // 写一次最新快照 + 启动轮询
      this.persistRegenQueue();
      if ((this.regenQueue || []).some(j => j.status === 'pending' || j.status === 'running')) {
        this._startRegenPolling();
      }
    },
    confirmDeleteRole(item) {
      if (!item) return;
      if (!window.confirm(`确认删除角色「${item.name || "图片"}」吗？`)) return;
      const wasActive = this.activeRoleId === item.id;
      this.roles = this.roles.filter(r => r.id != item.id);
      this.persistResourceCaches();
      if (wasActive) {
        this.activeRoleId = this.roles[0] ? this.roles[0].id : null;
      }
    },
    async addResourceToAssetLibrary(item) {
      if (!item) return;
      const eid = Number(item.id || 0);
      if (!eid) {
        this.windowAlert("仅已保存到服务端的资源才能添加到资产库，请先点击「保存角色/场景/素材」后再试。");
        return;
      }
      try {
        const resp = await userApi.addEntityToAssetLibrary(eid);
        if (!resp || resp.code != 0) {
          this.windowAlert((resp && resp.msg) || "添加到资产库失败");
          return;
        }
        this.windowAlert(resp.msg || "已添加到资产库");
      } catch (err) {
        this.windowAlert("添加到资产库异常：" + (err && err.message ? err.message : String(err)));
      }
    },
    openStoryboardPreview(item) {
      if (!item) return;
      const cover = String(item.cover || "").trim();
      if (!cover || this.isPlaceholderAsset(cover)) return;
      this.storyboardCoverPreview = {
        visible: true,
        src: cover,
        alt: String(item.title || "预览"),
        item
      };
    },
    openStoryboardEdit(item) {
      // 第三步的“编辑”改为跳转独立页面，而不是继续使用弹窗。
      this.persistStoryboardsCache();
      this.$router.push({
        path: "/project/storyboard-editor",
        query: {
          id: item.id,
          projectId: this.project.id
        }
      });
    },
    async openStoryboardExport(item) {
      await this.downloadStoryboardVideos(item);
    },
    mediaUrl(src) {
      const s = String(src || "").trim();
      if (!s) return "";
      if (/^https?:\/\//i.test(s)) return s;
      return s.startsWith("/") ? s : `/${s}`;
    },
    resolveStoryboardShots(item) {
      if (!item || typeof item !== "object") return [];
      if (Array.isArray(item.shots) && item.shots.length) return item.shots;
      const sid = Number(item.id || 0);
      if (sid <= 0) return [];
      const sb = (this.storyboards || []).find(row => Number(row && row.id) === sid);
      return sb && Array.isArray(sb.shots) ? sb.shots : [];
    },
    async fetchStoryboardShotsForExport(item) {
      let shots = this.resolveStoryboardShots(item);
      const pid = this.project && this.project.id ? Number(this.project.id) : 0;
      if (pid > 0 && localStorage.getItem("user_token")) {
        try {
          const row = await userApi.projectDetail(pid);
          const merged = this.mergeStoryboardsWithEpisodesShots(
            Array.isArray(row.storyboards) ? row.storyboards : [],
            Array.isArray(row.episodes) ? row.episodes : []
          );
          const sid = Number(item && item.id);
          const found = merged.find(sb => Number(sb && sb.id) === sid);
          if (found && Array.isArray(found.shots)) {
            shots = found.shots;
            const idx = (this.storyboards || []).findIndex(sb => Number(sb && sb.id) === sid);
            if (idx >= 0) {
              this.$set(this.storyboards, idx, { ...this.storyboards[idx], shots: found.shots });
            }
          }
        } catch (e) {
          /* 离线或未登录时沿用本地 shots */
        }
      }
      return Array.isArray(shots) ? shots : [];
    },
    collectStoryboardVideoExports(item, shots) {
      const safeTitle = String((item && item.title) || "分集")
        .replace(/[\\/:*?"<>|]/g, "_")
        .replace(/\s+/g, " ")
        .trim();
      const list = Array.isArray(shots) ? shots : [];
      const out = [];
      list.forEach((shot, idx) => {
        if (!shot || typeof shot !== "object") return;
        const raw = String(shot.videoUrl || shot.video_url || "").trim();
        if (!raw) return;
        const shotNo = Number(shot.no || shot.shot_no || shot.shotNo || idx + 1);
        const shotLabel = String(shot.title || "").trim();
        const namePart = shotLabel
          ? `${safeTitle || "分集"}-镜头${String(shotNo).padStart(2, "0")}-${shotLabel}`
          : `${safeTitle || "分集"}-镜头${String(shotNo).padStart(2, "0")}`;
        out.push({
          rawUrl: raw,
          shotNo,
          filename: `${namePart.replace(/[\\/:*?"<>|]/g, "_")}.mp4`
        });
      });
      return out;
    },
    async downloadSingleStoryboardVideo({ rawUrl, filename }) {
      const url = this.mediaUrl(rawUrl);
      if (!url) return false;
      let credentials = "omit";
      try {
        if (typeof location !== "undefined") {
          const u = new URL(url, location.href);
          if (u.origin === location.origin) credentials = "include";
        }
      } catch (e) {
        credentials = "omit";
      }
      try {
        const res = await fetch(url, { mode: "cors", credentials });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
        return true;
      } catch (e) {
        return false;
      }
    },
    async downloadStoryboardVideos(item) {
      if (!item) return;
      const shots = await this.fetchStoryboardShotsForExport(item);
      const entries = this.collectStoryboardVideoExports(item, shots);
      if (!entries.length) {
        this.windowAlert("当前分集暂无已生成的视频，无法导出。");
        return;
      }
      let failCount = 0;
      for (let i = 0; i < entries.length; i += 1) {
        const ok = await this.downloadSingleStoryboardVideo(entries[i]);
        if (!ok) failCount += 1;
        if (i < entries.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 400));
        }
      }
      if (failCount > 0) {
        this.windowAlert(
          `共 ${entries.length} 个视频，其中 ${failCount} 个因跨域限制未能自动下载。\n请在新标签打开视频链接后，右键「视频另存为」。`
        );
      }
    },
  }
};
</script>

<style scoped>
.setup-page {
  height: calc(100vh - 10px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-left:10px;
}

.setup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.app-page-header {
  padding: 10px 16px;
  background: var(--app-bg-elev-1);
  border: 1px solid var(--app-border-muted);
  border-radius: var(--app-radius-md);
  box-shadow: var(--app-shadow-card);
}
/* 设置页头：步骤条 + 进度条上下两行，互不重叠 */
.setup-header {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
.app-page-header-tabs {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.setup-header .step {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}
.setup-header .step[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}
.setup-header .step.done {
  color: #2c7a51;
}
.step-check {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--app-text-faint);
  display: inline-block;
}
.step-check.done {
  background: var(--app-text);
}
.step-check.running {
  background: var(--app-accent);
  animation: stepPulse 1.4s ease-in-out infinite;
}
@keyframes stepPulse {
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
}
.step-done-mark {
  color: var(--app-text);
  font-weight: 700;
}
.setup-header-progress {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  padding: 6px 10px;
  border-radius: var(--app-radius-md);
  background: var(--app-bg-elev-2);
  border: 1px solid var(--app-border);
  box-sizing: border-box;
}
.setup-header-progress-bar {
  height: 6px;
  border-radius: 3px;
  background: var(--app-bg-elev-3);
  overflow: hidden;
}
.setup-header-progress-bar > span {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, var(--app-primary), var(--app-accent));
  transition: width 0.4s ease;
}
.setup-header-progress-text {
  font-size: 14px;
  color: var(--app-text-muted);
}

.setup-offline-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 16px;
  padding: 4px 10px;
  border-radius: 5px;
  background: rgba(250, 173, 20, 0.16);
  border: 1px solid rgba(250, 173, 20, 0.45);
  color: #b65c00;
  font-size: 14px;
}
.offline-badge-text {
  white-space: nowrap;
}
.offline-badge-btn {
  background: #faad14;
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 2px 8px;
  cursor: pointer;
  font-size: 14px;
}
.offline-badge-btn.ghost {
  background: transparent;
  color: #b65c00;
  border: 1px solid rgba(250, 173, 20, 0.5);
}

.setup-fixed-actions {
  position: absolute;
  top: 20px;
  right: 18px;
  z-index: 5;
  display: flex;
  gap: 10px;
  align-items: center;
}

.setup-fixed-actions .main-btn,
.setup-fixed-actions .secondary-btn {
  width: auto;
  flex-shrink: 0;
}

.step1-processing-tip {
  padding: 6px 10px;
  border-radius: 10px;
  font-size: 14px;
  color: #b8f0cf;
  background: rgba(29, 125, 82, 0.2);
  border: 1px solid rgba(29, 125, 82, 0.45);
}

.step2-task-queue-hint {
  margin: 8px 0 10px;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.45;
  color: #d4e8ff;
  background: rgba(45, 108, 168, 0.22);
  border: 1px solid rgba(90, 160, 230, 0.45);
}

.step-fill {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.step {
  min-width: 110px;
  padding: 8px 14px;
  background: var(--app-bg-elev-1);
  border: 1px solid var(--app-border);
  border-radius: 999px;
  color: var(--app-text-soft);
  text-align: center;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}
.step:hover:not([disabled]) {
  border-color: var(--app-primary-border);
  color: var(--app-text);
}
.step.active {
  color: var(--app-accent-text);
  background: var(--app-accent);
  border-color: var(--app-accent);
  font-weight: 600;
}
.step.done { color: var(--app-text); }

.setup-body {
  display: grid;
  grid-template-columns: 500px 1fr;
  gap: 10px;
  height: 100%;
  min-height: 0;
}

.setup-sidebar,
.setup-main {
  background: var(--app-bg-card);
  border-radius: var(--app-radius-lg);
  padding: var(--app-spacing-md);
  min-height: 0;
  border: 1px solid var(--app-border-muted);
}

.setup-sidebar {
  overflow-y: auto;
}

.setup-main {
  overflow-y: auto;
}

.setup-block + .setup-block {
  margin-top: var(--app-spacing-lg);
}

.setup-block {
  padding: var(--app-spacing-sm) 0;
}

.project-info-card {
  background: var(--app-bg-elev-2);
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-md);
  padding: 14px;
  margin-bottom: 16px;
}
.project-info-card .setup-label {
  color: var(--app-text-soft);
}
.project-info-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.project-info-chip {
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--app-bg-elev-3);
  color: var(--app-text-soft);
  border: 1px solid var(--app-border);
}
.project-info-chip--style {
  background: rgba(255, 170, 0, 0.12);
  color: var(--app-warn);
  border-color: rgba(255, 170, 0, 0.3);
}

.style-reference-block {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed var(--app-border);
}
.style-reference-label {
  margin-bottom: 8px;
}
.style-reference-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}
.style-reference-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px;
  border-radius: var(--app-radius-sm);
  background: var(--app-bg-elev-2);
  border: 1px solid var(--app-border);
}
.style-reference-item img,
.style-reference-cover {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: var(--app-radius-sm);
  background: var(--app-bg-elev-3);
}
.style-reference-item span {
  font-size: 14px;
  color: #2a4a8b;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.setup-label {
  margin-bottom: 8px;
  color: var(--app-text-muted);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.2px;
}

.setup-hint {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 14px;
  line-height: 1.55;
  color: var(--app-text-soft);
}

.setup-title-input {
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  padding: 0 12px;
  border-radius: var(--app-radius-md);
  border: 1px solid var(--app-border);
  background: var(--app-bg-elev-1);
  color: var(--app-text);
  font-family: inherit;
  font-size: 14px;
  transition: border-color 0.18s, box-shadow 0.18s;
}
.setup-title-input:focus {
  outline: none;
  border-color: var(--app-primary);
  box-shadow: 0 0 0 3px var(--app-primary-soft);
}

.setup-select-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.setup-ratio-grid .setup-select-card {
  min-height: 48px;
}

.setup-select-card {
  background: var(--app-bg-elev-2);
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-md);
  color: var(--app-text);
  cursor: pointer;
  padding: 12px;
  text-align: left;
  transition: border-color 0.18s, box-shadow 0.18s, background 0.18s, transform 0.05s;
}
.setup-select-card:hover {
  border-color: var(--app-primary-border);
  background: var(--app-bg-elev-3);
}
.setup-select-card:active {
  transform: translateY(1px);
}
.setup-select-card.active {
  border-color: var(--app-primary);
  background: var(--app-primary-soft);
  box-shadow: 0 0 0 2px var(--app-primary-soft);
}

.setup-type-card {
  min-height: 70px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.setup-type-card span {
  color: var(--app-text-soft);
  font-size: 14px;
}

.setup-value {
  height: 40px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-radius: 8px;
  background: #1f1f1f;
}

.script-type-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.script-type {
  background: #1f1f1f;
  color: #d9d9d9;
  border: 1px solid #3f3f3f;
  border-radius: 8px;
  padding: 10px 14px;
}

.script-type.active,
.style-card.active {
  border-color: #19b06b;
  box-shadow: inset 0 0 0 1px #19b06b;
}

.script-type.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.style-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 1px;
}

.style-card {
  background: var(--app-bg-elev-2);
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-md);
  padding: 5px;
  cursor: pointer;
  transition: border-color 0.18s, box-shadow 0.18s, transform 0.18s;
}
.style-card:hover {
  border-color: var(--app-primary-border);
  transform: translateY(-1px);
}
.style-card.active {
  border-color: var(--app-primary);
  box-shadow: 0 0 0 2px var(--app-primary-soft);
}

.style-card-cover {
  width: 100%;
  border-radius: 10px;
  margin-bottom: 6px;
  aspect-ratio: 1 / 1;
  object-fit: cover;
}

.style-card span {
  display: block;
  text-align: center;
  font-size: 14px;
}

.script-editor {
  width: 100%;
  min-height: 360px;
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-md);
  background: var(--app-bg-elev-1);
  color: var(--app-text);
  padding: 14px;
  resize: none;
  margin-top: 14px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.6;
  transition: border-color 0.18s, box-shadow 0.18s;
}
.script-editor:focus {
  outline: none;
  border-color: var(--app-primary);
  box-shadow: 0 0 0 3px var(--app-primary-soft);
}

.script-editor.disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.script-char-count {
  margin-top: 8px;
  font-size: 14px;
  color: var(--app-text-soft);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.script-char-count.warn { color: var(--app-warn); }
.script-char-count.over { color: var(--app-danger); }

.script-char-hint { font-size: 14px; }

.script-episode-block {
  margin-top: 16px;
}

.episode-count-hint {
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.45;
  color: #9f9f9f;
}

.episode-count-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  min-height: 40px;
}

.episode-radios {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 18px;
  min-height: 40px;
  width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.episode-radios::-webkit-scrollbar {
  display: none;
}

/** 与「自定义集数」单选同一行，输入紧挨文字右侧 */
.episode-custom-inline {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  flex-shrink: 0;
}

.episode-radio-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #d6d6d6;
  font-size: 14px;
  user-select: none;
}

.episode-radio-label input[type="radio"] {
  width: 16px;
  height: 16px;
  accent-color: #19b06b;
  cursor: pointer;
}

.episode-count-input-wrap {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.episode-count-input {
  width: 72px;
  height: 36px;
  box-sizing: border-box;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid #3d3d3d;
  background: #1f1f1f;
  color: #fff;
  font-size: 14px;
}

.episode-count-input:focus {
  outline: none;
  border-color: #19b06b;
}

.episode-count-suffix {
  color: #9f9f9f;
  font-size: 14px;
}

.setup-bottom-bar {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
}

.setup-submit {
  max-width: 220px;
}

.setup-bottom-bar,
.footer-step-actions {
  display: none;
}

.empty-panel {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9c9c9c;
  background: #232323;
  border: 1px dashed #3c3c3c;
  border-radius: 10px;
}

.role-layout {
  display: grid;
  grid-template-columns: 320px 1fr 320px;
  gap: 10px;
  height: 100%;
  min-height: 0;
}

.role-list-panel,
.role-preview-panel,
.role-detail-panel,
.storyboard-layout {
  background: #333333;
  border-radius: 5px;
  padding: 10px;
  min-height: 0;
  overflow: hidden;
}

.role-list-panel {
  display: flex;
  flex-direction: column;
}

.role-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
  row-gap: 10px;
  min-width: 0;
}

.role-panel-header h3 {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.role-header-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  //margin-left: auto;
}

.role-filter-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #5b6470;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f5f7fa;
  border: 1px solid #e2e6ec;
  cursor: pointer;
  user-select: none;
}
.role-filter-toggle input { margin: 0; }
.role-filter-toggle.on {
  background: #e8f4ff;
  border-color: #4f8cff;
  color: #1c4c8a;
}
.role-filter-toggle em {
  font-style: normal;
  font-size: 14px;
  color: #4f8cff;
  font-weight: 600;
}

.resource-subnav {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
  padding: 4px 8px;
  background: #252525;
  border-radius: 10px;
  border: 1px solid #3b3b3b;
  min-width: 0;
  flex-wrap: wrap;
}

.resource-subnav-item {
  flex: 1;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #b8b8b8;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.resource-subnav-item:hover {
  color: #e6e6e6;
  background: rgba(255, 255, 255, 0.04);
}

.resource-subnav-item.active {
  background: #303030;
  color: #19b06b;
  box-shadow: inset 0 0 0 1px #19b06b55;
}

.role-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.role-list-item {
  position: relative;
  display: flex;
  gap: 10px;
  padding: 10px;
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-md);
  background: var(--app-bg-elev-2);
  cursor: pointer;
  transition: border-color 0.18s, box-shadow 0.18s, transform 0.18s;
}
.role-list-item:hover {
  border-color: var(--app-primary-border);
  transform: translateY(-1px);
}
.role-list-item.active {
  border-color: var(--app-primary);
  box-shadow: 0 0 0 2px var(--app-primary-soft);
}

.role-delete-icon {
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #5a3a3a;
  background: rgba(0, 0, 0, 0.35);
  color: #f0b0b0;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.role-delete-icon:hover {
  background: #3a2222;
}

.role-asset-icon {
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #2a4a3a;
  background: rgba(0, 0, 0, 0.35);
  color: #b0f0c0;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.role-asset-icon:hover {
  background: #1a3a2a;
}

.role-list-item.active {
  border-color: #19b06b;
  box-shadow: inset 0 0 0 1px #19b06b;
}

.role-list-thumb {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 10px;
  background: #f0f0f0;
}

.role-list-content {
  flex: 1;
  min-width: 0;
}

.role-list-top {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}
.role-list-top strong{
flex:1;
font-size: 14px;
}

.role-list-content p {
  margin: 0;
  font-size: 14px;
  color: #a9a9a9;
  line-height: 1.5;
}

.role-status {
  color: #19b06b;
  font-size: 14px;
}

.role-preview-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  min-height: 0;
}

.role-preview-panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.role-preview-image {
  width: 100%;
  //max-width: 550px;
  min-width:300px;
  flex: 1;
  min-height: 0;
  object-fit: contain;
  border-radius: 10px;
  cursor: zoom-in;
}

/* 复用 RechargeModal 的关闭按钮风格 */
.modal-close {
  position: fixed;
  right: 16px;
  top: 14px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.3);
  color: #fff;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  z-index: 10051;
}

.placeholder-block {
  background: #000 !important;
}

.role-preview-placeholder {
  flex: 1;
  min-height: 200px;
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 10px;
  border-radius: 10px;
  border: 1px dashed #4a4a4a;
  color: #9a9a9a;
  font-size: 13px;
  line-height: 1.5;
  box-sizing: border-box;
}

.role-preview-actions {
  margin-top: 14px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  flex-shrink: 0;
}

.role-empty,
.storyboard-empty {
  min-height: 420px;
}

.role-detail-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: 100%;
  overflow-y: auto;
}

.detail-field label {
  display: block;
  margin-bottom: 8px;
  color: #dfdfdf;
}

.detail-field input,
.detail-field textarea {
  width: 100%;
  background: #1f1f1f;
  border: 1px solid #3c3c3c;
  border-radius: 8px;
  color: #fff;
  padding: 10px 12px;
}

.detail-field textarea {
  resize: vertical;
}

.detail-actions {
  display: flex;
  gap: 10px;
  font-size:12px;
}
.main-btn{
  font-size:12px;
  min-width:70px;
}
.role-regenerate-points {
  margin-left: 4px;
  font-size: 14px;
  font-weight: 400;
}

.storyboard-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 18px;
  flex-shrink: 0;
}

.storyboard-header-left h2 {
  margin: 0 0 6px;
}

.storyboard-header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;
}

.storyboard-layout {
  display: flex;
  flex-direction: column;
}

.storyboard-content-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.batch-btn {
  max-width: 140px;
}

.storyboard-header-actions .batch-btn {
  max-width: none;
}

.storyboard-view-toggle {
  display: inline-flex;
  background: #f5f7fa;
  border: 1px solid #d2e2f3;
  border-radius: 8px;
  overflow: hidden;
}
.storyboard-view-toggle-item {
  padding: 5px 12px;
  font-size: 13px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #5b6470;
  transition: all 0.2s;
}
.storyboard-view-toggle-item.active {
  background: #4f8cff;
  color: #fff;
}

.storyboard-table {
  border: 1px solid #e2ecf8;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}
.storyboard-table-head,
.storyboard-table-row {
  display: grid;
  grid-template-columns: 56px 1.4fr 2fr 80px 200px;
  align-items: center;
}
.storyboard-table-head {
  background: #f5f7fa;
  font-weight: 600;
  color: #2a4a8b;
  font-size: 13px;
}
.storyboard-table-row {
  border-top: 1px solid #eef2f8;
  font-size: 13px;
  color: #2c3e50;
  transition: background 0.2s;
}
.storyboard-table-row:hover {
  background: #f9fbff;
}
.storyboard-table-cell {
  padding: 10px 12px;
  word-break: break-all;
  overflow: hidden;
  text-overflow: ellipsis;
}
.storyboard-table-cell--no {
  color: #5b6470;
  text-align: center;
}
.storyboard-table-cell--ops {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.storyboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(520px, 1fr));
  gap: var(--app-spacing-lg);
}

.storyboard-card {
  display: grid;
  grid-template-columns: 24px 150px 1fr;
  gap: var(--app-spacing-md);
  padding: var(--app-spacing-md);
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-lg);
  background: var(--app-bg-elev-2);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}
.storyboard-card:hover {
  border-color: var(--app-primary-border);
  box-shadow: var(--app-shadow-card);
  transform: translateY(-1px);
}

.storyboard-index {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d0d0d0;
  font-weight: 700;
}

.storyboard-cover {
  width: 150px;
  height: 100px;
  border-radius: 10px;
  object-fit: cover;
}

.storyboard-content h3 {
  margin: 0 0 10px;
  font-size: 16px;
}

.storyboard-meta {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  color: #a9a9a9;
  font-size: 13px;
}

.storyboard-actions {
  display: flex;
  gap: 10px;
  margin-top: 18px;
  flex-wrap: wrap;
}

.story-btn {
  border: 1px solid var(--app-border);
  border-radius: 999px;
  background: var(--app-bg-elev-1);
  color: var(--app-text);
  padding: 6px 14px;
  cursor: pointer;
  font-size: 13px;
  font-family: inherit;
  transition: border-color 0.18s, background 0.18s, color 0.18s, transform 0.05s;
  white-space: nowrap;
}
.story-btn:hover {
  border-color: var(--app-primary-border);
  color: var(--app-text);
  background: var(--app-bg-elev-3);
}
.story-btn:active { transform: translateY(1px); }
.story-btn.primary {
  background: var(--app-primary-soft);
  border-color: var(--app-primary-border);
  color: var(--app-primary);
}
.story-btn.story-btn-danger {
  border-color: var(--app-danger);
  color: var(--app-danger);
  background: var(--app-danger-soft);
}
.story-btn:disabled,
.story-btn[disabled] {
  opacity: 0.55;
  cursor: not-allowed;
  pointer-events: none;
}
.story-btn-progress {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 14px;
  color: var(--app-text-muted, var(--app-text-secondary, #999));
}
.story-btn-progress--block {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  margin-top: 8px;
  width: 100%;
}
.story-btn-progress-bar {
  flex: 1;
  height: 4px;
  background: var(--app-border, rgba(255, 255, 255, 0.08));
  border-radius: 4px;
  overflow: hidden;
  min-width: 80px;
}
.story-btn-progress-fill {
  height: 100%;
  background: var(--app-primary, #6366f1);
  border-radius: 4px;
  transition: width 0.4s ease;
}
.story-btn-progress-text {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.regen-toast {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1700;
  width: 320px;
  max-width: calc(100vw - 48px);
  background: var(--app-bg-elev-2, #15171a);
  border: 1px solid var(--app-border, rgba(255, 255, 255, 0.08));
  border-radius: 12px;
  padding: 14px 14px 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
  color: var(--app-text, #ddd);
  font-size: 13px;
}
.regen-toast-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.regen-toast-count {
  font-size: 14px;
  color: var(--app-text-muted, #999);
}
.regen-toast-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 8px;
  padding: 8px 0;
  border-top: 1px dashed var(--app-border, rgba(255, 255, 255, 0.08));
}
.regen-toast-row:first-of-type {
  border-top: none;
}
.regen-toast-title {
  font-weight: 600;
  grid-column: 1 / 2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.regen-toast-status {
  grid-column: 2 / 3;
  align-self: center;
  font-size: 14px;
  color: var(--app-text-muted, #999);
}
.regen-toast-bar {
  grid-column: 1 / 3;
  height: 4px;
  background: var(--app-border, rgba(255, 255, 255, 0.08));
  border-radius: 4px;
  overflow: hidden;
}
.regen-toast-fill {
  height: 100%;
  background: var(--app-primary, #6366f1);
  border-radius: 4px;
  transition: width 0.4s ease;
}
.regen-toast-message {
  grid-column: 1 / 3;
  font-size: 14px;
  color: var(--app-text-muted, #999);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.regen-toast-row--running .regen-toast-fill {
  background: linear-gradient(90deg, var(--app-primary, #6366f1), var(--app-primary-strong, #818cf8));
  animation: regen-bar-shine 1.4s linear infinite;
}
.regen-toast-row--done .regen-toast-fill {
  background: #22c55e;
}
.regen-toast-row--fail .regen-toast-fill {
  background: #ef4444;
}
@keyframes regen-bar-shine {
  0% { filter: brightness(1); }
  50% { filter: brightness(1.4); }
  100% { filter: brightness(1); }
}

.modal-field-block {
  margin-top: 14px;
}

.modal-field-block label {
  display: block;
  margin-bottom: 8px;
  color: var(--app-text-muted);
  font-size: 13px;
  font-weight: 500;
}

.modal-field-block input {
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #3d3d3d;
  background: #1f1f1f;
  color: #fff;
}

.episode-modal .modal-field-block .episode-modal-script {
  min-height: 200px;
  max-height: min(360px, 50vh);
  margin-top: 0;
  overflow-y: auto;
  resize: vertical;
}

.episode-content-count {
  margin-top: 6px;
  text-align: right;
  font-size: 14px;
  color: #8c8c8c;
}

.modal-textarea {
  width: 100%;
  background: #1f1f1f;
  border: 1px solid #444;
  border-radius: 8px;
  color: #fff;
  padding: 10px;
  resize: vertical;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 0px;
}

.confirm-modal {
  max-width: 460px;
}
.confirm-modal .confirm-message {
  margin: 12px 0 24px;
  line-height: 1.6;
  color: #cfcfcf;
  white-space: pre-wrap;
  word-break: break-word;
}
.confirm-modal .modal-actions {
  margin-top: 8px;
}

.storyboard-summary {
  margin: 4px 0 8px;
  font-size: 13px;
  color: #9a9a9a;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.modal-main-btn {
  max-width: 150px;
}

.modal-inline-fields {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.storyboard-export-wrap {
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@media (max-width: 768px) {
  .setup-page {
    height: auto;
    min-height: calc(100vh - 40px);
    max-height: none;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .step-fill {
    overflow: visible;
    min-height: min(70vh, 520px);
  }

  .setup-header {
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    padding: 0 8px;
    margin-bottom: 12px;
  }

  .setup-fixed-actions {
    position: static;
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
    padding: 0 10px 10px;
    box-sizing: border-box;
  }

  .step {
    min-width: 0;
    flex: 1 1 calc(33.333% - 10px);
    padding: 8px 5px;
    font-size: 14px;
  }

  .setup-body {
    grid-template-columns: 1fr;
    gap: 12px;
    height: auto;
    min-height: 0;
  }

  .setup-sidebar,
  .setup-main {
    min-height: 0;
    overflow: visible;
  }

  .style-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .episode-count-row,
  .episode-radios {
    flex-wrap: wrap;
  }

  .episode-custom-inline {
    flex-wrap: wrap;
  }

  .role-layout {
    grid-template-columns: 1fr;
    height: auto;
    min-height: 0;
  }

  .role-panel-header {
    flex-wrap: wrap;
    align-items: flex-start;
  }

  .role-panel-header h3 {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    width: 100%;
  }

  .role-header-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .role-preview-image {
    min-width: 0;
    max-width: 100%;
  }

  .role-preview-placeholder {
    max-width: none;
    min-height: 160px;
  }

  .role-empty,
  .storyboard-empty {
    min-height: 220px;
  }

  .detail-actions {
    flex-wrap: wrap;
  }

  .storyboard-header {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .storyboard-header-actions {
    justify-content: flex-start;
  }

  .storyboard-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .storyboard-card {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  .storyboard-index {
    align-self: flex-start;
  }

  .storyboard-cover {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
    max-width: none;
  }

  .modal-inline-fields {
    grid-template-columns: 1fr;
  }

  .resource-type-selector {
    display: flex;
    gap: 12px;
    margin-top: 4px;
    flex-wrap: wrap;
  }

  .resource-type-selector label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    padding: 4px 12px;
    border: 1px solid #d0d5dd;
    border-radius: 4px;
    font-size: 14px;
    color: #555;
    transition: all 0.15s;
    user-select: none;
    line-height: 1.4;
  }

  .resource-type-selector label input[type="radio"] {
    margin: 0;
    flex-shrink: 0;
    cursor: pointer;
  }

  .resource-type-selector label.active {
    border-color: #4f7cff;
    background: #eef2ff;
    color: #4f7cff;
    font-weight: 500;
  }
}
</style>
